import React, { useState, useEffect, useMemo } from 'react';
import './custom-alerts.css';
import { AlertCircle, Trash2, DollarSign, Users, Calendar, Package, Bell, Settings, CreditCard, FileText, TrendingUp, Plus, Edit, X, Check, Download, Upload, Search, Filter, Mail, MapPin, Phone, Home, Building, User, XCircle, Menu, ChevronDown, ChevronUp, RotateCcw } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const API_BASE = "";

// Maps internal invoice status to display label
const invoiceStatusLabel = (status) => {
  if (status === 'pending') return 'Unpaid';
  if (status === 'processing') return 'Pending';
  if (status === 'paid') return 'Paid';
  if (status === 'overdue') return 'Overdue';
  return status ? status.charAt(0).toUpperCase() + status.slice(1) : '';
};

const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_live_51TaHlTAIWr74b0ytCvZxx8kcFIeAZDxwtHVdjosqtbXehXvy6aCZUTMc7v4fcB7YBp3Z6Ms52m2fLoHIzkUF6NSN00b6E5fHrk';
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

// ---- Custom Alert/Confirm System ----
let showAlert, showConfirm;

const safeShowAlert = (message) => {
  if (typeof showAlert === 'function') {
    showAlert(message);
  } else {
    // Fallback: use browser alert if custom one isn't ready
    alert(message);
  }
};

const CustomAlert = ({ message, onClose }) => {
  if (!message) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl animate-scale-in">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 rounded-full bg-[#dce4f5] flex items-center justify-center">
              <Check className="w-6 h-6 text-[#1B2D5C]" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Notice</h3>
            <p className="text-gray-600">{message}</p>
          </div>
        </div>
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-[#1B2D5C] text-white rounded-lg hover:bg-[#0f1e3d] font-medium transition"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

const CustomConfirm = ({ message, onConfirm, onCancel }) => {
  if (!message) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl animate-scale-in">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm Action</h3>
            <p className="text-gray-600">{message}</p>
          </div>
        </div>
        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={onCancel}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-6 py-2 bg-[#1B2D5C] text-white rounded-lg hover:bg-[#0f1e3d] font-medium transition"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

// ---- Auth (frontend) ----
const auth = {
  getToken() {
    return localStorage.getItem('jwt') || '';
  },
  setToken(token) {
    if (token) localStorage.setItem('jwt', token);
  },
  clear() {
    localStorage.removeItem('jwt');
  },
  isAuthed() {
    return !!auth.getToken();
  },
  getRole() {
    try {
      // quick-n-dirty decode (no crypto): read payload to get role
      const token = auth.getToken();
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload?.role || null;
    } catch {
      return null;
    }
  },
};

// ---- Demo Users ----
const demoUsers = [
  { email: "admin@riffesanitation.com", password: "UncKn0wsB3st2026!@", role: "admin" },
  { email: "john@example.com", password: "customer123", role: "customer" },
];


const payInvoice = async (invoice, method = 'stripe') => {
  try {
    const res = await fetch('/api/pay/create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ invoiceId: invoice.id, method }),
    });
    const data = await res.json();
    if (data.url) {
      window.location.href = data.url; // Redirect to Stripe/PayPal checkout
    } else {
      safeShowAlert('Payment link not available.');
    }
  } catch (err) {
    console.error('Payment error:', err);
    safeShowAlert('Payment failed. Please try again.');
  }
};



// ===========================================
// REAL BACKEND STORAGE LAYER  (SQLite → API)
// ===========================================
const storage = {
  async get(key) {
    try {
      const res = await fetch(`/api/${key}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${auth.getToken()}` }
      });
      if (!res.ok) throw new Error(`GET /api/${key} failed`);
      return await res.json();
    } catch (err) {
      console.error(`[storage.get] ${key} error:`, err);
      return null;
    }
  },

  async set(key, value) {
    try {
      const res = await fetch(`/api/${key}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify(value)
      });
      if (!res.ok) throw new Error(`POST /api/${key} failed`);
      return true;
    } catch (err) {
      console.error(`[storage.set] ${key} error:`, err);
      return false;
    }
  },

  async delete(key, id) {
    try {
      const res = await fetch(`/api/${key}/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${auth.getToken()}` }
      });
      return res.ok;
    } catch (err) {
      console.error(`[storage.delete] ${key}/${id} error:`, err);
      return false;
    }
  }
};






// Initialize demo data
// ============================================================
// SAFE INITIALIZE DATA (DEV MODE ONLY - DOES NOT OVERWRITE DB)
// Works with Create React App (uses process.env.NODE_ENV)
// ============================================================
// ============================================================
// DISABLE DEMO DATA COMPLETELY
// Backend is now the ONLY source of truth
// ============================================================
const initializeData = async () => {
  console.log("[INIT] Demo mode disabled — using real backend only.");
  return;
};



// Main App Component
// Main App Component
const TrashCollectionApp = () => {
  const [userRole, setUserRole] = useState(auth.getRole()); // 'admin' | 'customer' | null
  const [loading, setLoading] = useState(true);
  const [resetToken, setResetToken] = useState(null);
  const [resetSuccess, setResetSuccess] = useState(false);

  // Custom Alert/Confirm State
  const [alertMessage, setAlertMessage] = useState(null);
  const [confirmConfig, setConfirmConfig] = useState(null);

  // Expose global alert/confirm functions
  useEffect(() => {
    showAlert = (message) => {
      setAlertMessage(message);
    };

    showConfirm = (message) => {
      return new Promise((resolve) => {
        setConfirmConfig({
          message,
          onConfirm: () => {
            setConfirmConfig(null);
            resolve(true);
          },
          onCancel: () => {
            setConfirmConfig(null);
            resolve(false);
          }
        });
      });
    };
  }, []);

  useEffect(() => {
    const init = async () => {
      // Check for password reset token in URL
      const params = new URLSearchParams(window.location.search);
      const token = params.get('reset');
      if (token) {
        setResetToken(token);
        // Clean the URL without refreshing
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      await initializeData();
      setLoading(false);
    };
    init();
  }, []);

  const handleLogout = () => {
    auth.clear();
    setUserRole(null);
  };

  const handleResetClose = () => {
    setResetToken(null);
    setResetSuccess(false);
  };

  const handleResetSuccess = () => {
    setResetToken(null);
    setResetSuccess(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Trash2 className="w-16 h-16 text-[#1B2D5C] mx-auto mb-4 animate-pulse" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show password reset modal if token is present
  if (resetToken) {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center">
          <div className="text-center">
            <Trash2 className="w-16 h-16 text-[#1B2D5C] mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-800">Riffe Family Sanitation</h1>
          </div>
        </div>
        <PasswordResetModal
          token={resetToken}
          onClose={handleResetClose}
          onSuccess={handleResetSuccess}
        />
      </>
    );
  }

  // Show success message after password reset
  if (resetSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-[#dce4f5] rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-[#1B2D5C]" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Password Reset Successful</h2>
          <p className="text-gray-600 mb-6">
            Your password has been changed. You can now log in with your new password.
          </p>
          <button
            onClick={() => setResetSuccess(false)}
            className="bg-[#1B2D5C] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#0f1e3d]"
          >
            Continue to Login
          </button>
        </div>
      </div>
    );
  }

  if (!userRole) {
    return (
      <>
        <LandingPage onSelectRole={setUserRole} />
        {alertMessage && (
          <CustomAlert
            message={alertMessage}
            onClose={() => setAlertMessage(null)}
          />
        )}
        {confirmConfig && (
          <CustomConfirm
            message={confirmConfig.message}
            onConfirm={confirmConfig.onConfirm}
            onCancel={confirmConfig.onCancel}
          />
        )}
      </>
    );
  }

  if (userRole === 'admin') {
    return (
      <>
        <AdminPortal onLogout={handleLogout} />
        {alertMessage && (
          <CustomAlert
            message={alertMessage}
            onClose={() => setAlertMessage(null)}
          />
        )}
        {confirmConfig && (
          <CustomConfirm
            message={confirmConfig.message}
            onConfirm={confirmConfig.onConfirm}
            onCancel={confirmConfig.onCancel}
          />
        )}
      </>
    );
  }

  return (
    <>
      <CustomerPortal onLogout={handleLogout} />
      {alertMessage && (
        <CustomAlert
          message={alertMessage}
          onClose={() => setAlertMessage(null)}
        />
      )}
      {confirmConfig && (
        <CustomConfirm
          message={confirmConfig.message}
          onConfirm={confirmConfig.onConfirm}
          onCancel={confirmConfig.onCancel}
        />
      )}
    </>
  );
};
// Landing Page
const LandingPage = ({ onSelectRole }) => {
  const [showLogin, setShowLogin] = useState(false);
  const [notices, setNotices] = useState([]);

  useEffect(() => {
    loadNotices();
  }, []);

  const loadNotices = async () => {
    try {
      const res = await fetch(`/api/notices`);
      const data = await res.json();
      setNotices(data.filter(n => n.active));
    } catch (err) {
      console.error('Failed to load notices:', err);
    }
  };

  return (
    <div className="min-h-screen bg-white">

      {/* NAVBAR */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20 sm:h-28">

            {/* Logo */}
            <div className="flex items-center space-x-2">
              <img src="/logo.png" className="h-16 w-auto sm:h-24" alt="Riffe Family Sanitation" />
            </div>

            {/* AUTH BUTTONS */}
            <div className="flex items-center space-x-2 sm:space-x-4">

              {/* Signup */}
              <button
                onClick={() => setShowLogin("signup")}
                className="bg-[#dce4f5] text-[#0f1e3d] px-4 sm:px-6 py-2 rounded-lg hover:bg-[#c3d0ee] transition"
              >
                Sign Up
              </button>

              {/* Login */}
              <button
                onClick={() => setShowLogin("admin")}
                className="bg-[#1B2D5C] text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-[#0f1e3d] transition"
              >
                Login
              </button>

            </div>
          </div>
        </div>
      </nav>

      {/* NOTICES BANNER */}
      {notices.length > 0 && (
        <div className="bg-yellow-50 border-b-4 border-yellow-400">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            {notices.map(notice => (
              <div
                key={notice.id}
                className={`mb-3 last:mb-0 p-4 rounded-lg border-l-4 ${
                  notice.priority === 'high'
                    ? 'bg-red-50 border-red-500 text-red-900'
                    : notice.priority === 'medium'
                    ? 'bg-yellow-100 border-yellow-500 text-yellow-900'
                    : 'bg-blue-50 border-blue-500 text-blue-900'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <Bell className="w-6 h-6 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-bold text-lg mb-1">{notice.title}</h3>
                    <p className="text-sm">{notice.message}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* HERO SECTION */}
      <div className="relative bg-gradient-to-br from-[#1B2D5C] to-[#243d7a] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-24">
          <div className="max-w-3xl">
            <h1 className="text-3xl md:text-5xl font-bold mb-6">Leave The Garbage To Us</h1>
            <p className="text-lg md:text-xl mb-8 text-blue-50">
              Service Rooted in Faith — professional waste management you can trust.
            </p>

            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
              <button
                onClick={() => setShowLogin("customer")}
                className="bg-white text-[#1B2D5C] px-8 py-3 rounded-lg font-semibold hover:bg-[#eef1fa] transition"
              >
                Get Started
              </button>

              <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-[#1B2D5C] transition">
                View Services
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* SERVICES PREVIEW */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 md:mb-12 text-center">Our Services</h2>

        <div className="grid md:grid-cols-3 gap-4 md:gap-8">
          <ServiceCard 
            icon={<Home className="w-12 h-12" />}
            title="Residential Service"
            description="Weekly pickup for homes"
            price="Starting at $18/mo"
          />

          <ServiceCard 
            icon={<Building className="w-12 h-12" />}
            title="Commercial Service"
            description="Pickup for businesses"
            price="Starting at $20/mo"
          />

          <ServiceCard 
            icon={<Package className="w-12 h-12" />}
            title="Dumpster Rentals"
            description="2-8 yard dumpsters available"
            price="Starting at $55/mo"
          />
        </div>
      </div>

      {/* FEATURES SECTION */}
      <div className="bg-gray-50 py-10 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 md:mb-12 text-center">
            Why Choose Us
          </h2>

          <div className="grid md:grid-cols-4 gap-4 md:gap-8">
            <FeatureCard icon={<Calendar />} title="Reliable Pickup" description="Always on time" />
            <FeatureCard icon={<CreditCard />} title="Easy Payments" description="Secure online billing and payments" />
            <FeatureCard icon={<Bell />} title="Service Alerts" description="Get notified about schedule changes" />
            <FeatureCard icon={<Users />} title="Locally Owned" description="We're here for our community" />
          </div>
        </div>
      </div>

      {/* CALL TO ACTION */}
      <div className="bg-[#1B2D5C] text-white py-10 md:py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-lg md:text-xl mb-8 text-blue-50">Join thousands of satisfied customers today</p>

          <button
            onClick={() => setShowLogin("signup")}
            className="bg-white text-[#1B2D5C] px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition text-lg"
          >
            Create Account
          </button>
        </div>
      </div>

      {/* FOOTER */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="grid md:grid-cols-4 gap-4 md:gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="bg-white rounded-xl p-2 inline-block"><img src="/logo.png" className="h-14 w-auto" alt="Riffe Family Sanitation" /></div>
              </div>
              <p className="text-sm font-medium text-white">Service Rooted in Faith</p>
              <p className="text-xs text-gray-400 mt-1">1 Corinthians 10:31</p>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-3">Services</h3>
              <ul className="space-y-2 text-sm">
                <li>Residential Pickup</li>
                <li>Commercial Service</li>
                <li>Dumpster Rentals</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-3">Company</h3>
              <ul className="space-y-2 text-sm">
                <li>About Us</li>
                <li>Contact</li>
                <li>Careers</li>
                <li>Sustainability</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-3">Contact</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center space-x-2">
                  <Phone className="w-4 h-4" />
                  <span>(606) 770-4180</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4" />
                  <span>info@riffesanitation.com</span>
                </div>
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4" />
                  <span>Quincy, KY</span>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm">
            <p>&copy; 2026 Riffe Family Sanitation. All rights reserved.</p>
          </div>

        </div>
      </footer>

      {/* AUTH MODALS */}
      {showLogin === "customer" && (
        <LoginModal 
          onClose={() => setShowLogin(false)}
          onLogin={(role) => onSelectRole(role)}
        />
      )}

      {showLogin === "admin" && (
        <LoginModal 
          onClose={() => setShowLogin(false)}
          onLogin={(role) => onSelectRole(role)}
        />
      )}

      {showLogin === "signup" && (
        <SignupModal
          onClose={() => setShowLogin(false)}
          onSignup={() => onSelectRole("customer")}
        />
      )}

    </div>
  );
};


const ServiceCard = ({ icon, title, description, price }) => (
  <div className="bg-white p-5 md:p-8 rounded-xl shadow-lg hover:shadow-xl transition">
    <div className="text-[#1B2D5C] mb-4">{icon}</div>
    <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-600 mb-4">{description}</p>
    <p className="text-[#1B2D5C] font-semibold">{price}</p>
  </div>
);

const FeatureCard = ({ icon, title, description }) => (
  <div className="text-center">
    <div className="inline-block p-4 bg-[#dce4f5] rounded-full text-[#1B2D5C] mb-4">
      {icon}
    </div>
    <h3 className="font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-600 text-sm">{description}</p>
  </div>
);

// =========================
// Login Modal (with Forgot Password)
// =========================
const LoginModal = ({ onClose, onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState("customer"); // "customer" or "admin"
  const [view, setView] = useState("login"); // "login" or "forgot"
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");

    try {
      const res = await fetch(`/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          password,
          role: mode,
        }),
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.error || "Login failed");

      auth.setToken(data.token);
      onLogin(data.role);
      onClose();
    } catch (err) {
      setError(err.message);
    }

    setBusy(false);
  };

  const doForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      setError("Please enter your email address");
      return;
    }
    setBusy(true);
    setError("");
    setMessage("");

    try {
      const res = await fetch(`/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.error || "Failed to send reset email");

      setMessage("If an account exists with this email, a password reset link has been sent.");
      setEmail("");
    } catch (err) {
      setError(err.message);
    }

    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-8">
        {/* HEADER */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">
            {view === "forgot" ? "Reset Password" : mode === "admin" ? "Admin Login" : "Customer Login"}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {view === "login" ? (
          <>
            {/* MODE SWITCH */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setMode("customer")}
                className={`flex-1 py-2 rounded-lg border ${
                  mode === "customer" ? "bg-[#1B2D5C] text-white" : "bg-gray-100"
                }`}
              >
                Customer
              </button>

              <button
                onClick={() => setMode("admin")}
                className={`flex-1 py-2 rounded-lg border ${
                  mode === "admin" ? "bg-[#1B2D5C] text-white" : "bg-gray-100"
                }`}
              >
                Admin
              </button>
            </div>

            {/* ERROR */}
            {error && (
              <div className="mb-4 text-sm text-red-700 bg-red-100 rounded p-2">
                {error}
              </div>
            )}

            {/* LOGIN FORM */}
            <form onSubmit={doLogin} className="space-y-4">
              <input
                type="email"
                required
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border rounded-lg"
              />

              <input
                type="password"
                required
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border rounded-lg"
              />

              <button
                type="submit"
                disabled={busy}
                className="w-full bg-[#1B2D5C] text-white py-3 rounded-lg font-semibold"
              >
                {busy ? "Signing in…" : "Sign In"}
              </button>

              {mode === "customer" && (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => { setView("forgot"); setError(""); setMessage(""); }}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    Forgot your password?
                  </button>
                </div>
              )}

              {mode === "admin" && (
                <div className="text-xs text-center text-gray-500 pt-2">
                </div>
              )}
            </form>
          </>
        ) : (
          <>
            {/* FORGOT PASSWORD VIEW */}
            {error && (
              <div className="mb-4 text-sm text-red-700 bg-red-100 rounded p-2">
                {error}
              </div>
            )}

            {message && (
              <div className="mb-4 text-sm text-[#0f1e3d] bg-[#dce4f5] rounded p-3">
                {message}
              </div>
            )}

            <p className="text-gray-600 mb-4">
              Enter your email address and we'll send you a link to reset your password.
            </p>

            <form onSubmit={doForgotPassword} className="space-y-4">
              <input
                type="email"
                required
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border rounded-lg"
              />

              <button
                type="submit"
                disabled={busy}
                className="w-full bg-[#1B2D5C] text-white py-3 rounded-lg font-semibold"
              >
                {busy ? "Sending…" : "Send Reset Link"}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => { setView("login"); setError(""); setMessage(""); }}
                  className="text-sm text-gray-600 hover:underline"
                >
                  Back to login
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};


// =========================
// Password Reset Modal (when user clicks email link)
// =========================
const PasswordResetModal = ({ token, onClose, onSuccess }) => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState("");

  useEffect(() => {
    const verifyToken = async () => {
      try {
        const res = await fetch(`/api/auth/verify-reset-token/${token}`);
        const data = await res.json();
        if (data.valid) {
          setTokenValid(true);
          setEmail(data.email);
        } else {
          setError(data.error || "Invalid or expired reset link");
        }
      } catch (err) {
        setError("Failed to verify reset link");
      }
      setValidating(false);
    };
    verifyToken();
  }, [token]);

  const doReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setBusy(true);
    setError("");

    try {
      const res = await fetch(`/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Reset failed");

      onSuccess();
    } catch (err) {
      setError(err.message);
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Reset Your Password</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {validating ? (
          <div className="text-center py-8">
            <div className="animate-spin w-8 h-8 border-4 border-[#1B2D5C] border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600">Verifying reset link...</p>
          </div>
        ) : !tokenValid ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <X className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-red-600 mb-2">Invalid Reset Link</h3>
            <p className="text-gray-600 mb-4">{error || "This reset link is invalid or has expired."}</p>
            <button
              onClick={onClose}
              className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <p className="text-gray-600 mb-4">
              Enter a new password for <strong>{email}</strong>
            </p>

            {error && (
              <div className="mb-4 text-sm text-red-700 bg-red-100 rounded p-2">
                {error}
              </div>
            )}

            <form onSubmit={doReset} className="space-y-4">
              <input
                type="password"
                required
                placeholder="New password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 border rounded-lg"
                minLength={6}
              />

              <input
                type="password"
                required
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 border rounded-lg"
                minLength={6}
              />

              <button
                type="submit"
                disabled={busy}
                className="w-full bg-[#1B2D5C] text-white py-3 rounded-lg font-semibold hover:bg-[#0f1e3d]"
              >
                {busy ? "Resetting..." : "Reset Password"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};


// =========================
// Signup Modal (REAL backend customer registration)
// =========================
// =========================
// Signup Modal (PATCHED — uses onSignup instead of onCreated)
// =========================
const SignupModal = ({ onClose, onSignup }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [zip, setZip] = useState("");
  const [type, setType] = useState("residential");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [addrSuggestion, setAddrSuggestion] = useState(null);

  const doSignup = async (addr, cty, zp) => {
    setBusy(true);
    try {
      const res = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password, phone, address: addr, city: cty, zip: zp, type }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Signup failed');
      auth.setToken(data.token);
      if (typeof onSignup === 'function') onSignup('customer');
      onClose();
    } catch (err) {
      setError(err.message);
    }
    setBusy(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (address && city) {
      setBusy(true);
      try {
        const valRes = await fetch('/api/address/validate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ address, city, zip }),
        });
        const valData = await valRes.json();
        if (valData.match) {
          const m = valData.match;
          const isDifferent =
            m.address.toLowerCase() !== address.trim().toLowerCase() ||
            m.city.toLowerCase() !== city.trim().toLowerCase();
          if (isDifferent) {
            setAddrSuggestion(m);
            setBusy(false);
            return;
          }
        }
      } catch (_) {}
      setBusy(false);
    }

    await doSignup(address, city, zip);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-50">
      {addrSuggestion && (
        <AddressConfirmDialog
          current={{ address, city, zip }}
          suggested={addrSuggestion}
          onUse={() => {
            const m = addrSuggestion;
            setAddress(m.address);
            setCity(m.city);
            if (m.zip) setZip(m.zip);
            setAddrSuggestion(null);
            doSignup(m.address, m.city, m.zip || zip);
          }}
          onKeep={() => { setAddrSuggestion(null); doSignup(address, city, zip); }}
        />
      )}
      <div className="bg-white rounded-xl w-full max-w-lg p-8">
        <h2 className="text-2xl font-bold mb-4">Create Your Account</h2>

        {error && (
          <div className="p-2 mb-4 bg-red-100 text-red-600 rounded">{error}</div>
        )}

        <form className="space-y-4" onSubmit={handleSubmit}>
          <input
            className="w-full border p-3 rounded"
            placeholder="Full Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />

          <input
            className="w-full border p-3 rounded"
            placeholder="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />

          <input
            className="w-full border p-3 rounded"
            placeholder="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />

          <input
            className="w-full border p-3 rounded"
            placeholder="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />

          <input
            className="w-full border p-3 rounded"
            placeholder="Street Address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />

          <div className="flex gap-4">
            <input
              className="flex-1 border p-3 rounded"
              placeholder="City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />

            <input
              className="flex-1 border p-3 rounded"
              placeholder="ZIP"
              value={zip}
              onChange={(e) => setZip(e.target.value)}
            />
          </div>

          <select
            className="w-full border p-3 rounded"
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            <option value="residential">Residential</option>
            <option value="commercial">Commercial</option>
          </select>

          <button
            type="submit"
            disabled={busy}
            className="w-full bg-[#1B2D5C] text-white py-3 rounded-lg font-semibold"
          >
            {busy ? "Creating..." : "Create Account"}
          </button>
        </form>
      </div>
    </div>
  );
};





// Admin Portal
const AdminPortal = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [services, setServices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [notices, setNotices] = useState([]);
  const [allLocations, setAllLocations] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

const loadData = async () => {
  try {
    const [svc, cust, subs, inv, noticesData, locs] = await Promise.all([
      fetch(`/api/services`).then(r => r.json()),
      fetch(`/api/customers`).then(r => r.json()),
      fetch(`/api/subscriptions`).then(r => r.json()),
      fetch(`/api/invoices`).then(r => r.json()),
      fetch(`/api/notices`).then(r => r.json()),
      fetch(`/api/locations`).then(r => r.json()),
    ]);

    setServices(svc);
    setCustomers(cust);
    setSubscriptions(subs);
    setInvoices(inv);
    setNotices(noticesData);
    setAllLocations(locs);
  } catch (err) {
    console.error("loadData error:", err);
  }
};


  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: <TrendingUp className="w-5 h-5" /> },
    { id: 'requests', label: 'Requests', icon: <AlertCircle className="w-5 h-5" /> },
    { id: 'customers', label: 'Customers', icon: <Users className="w-5 h-5" /> },
    { id: 'services', label: 'Services', icon: <Package className="w-5 h-5" /> },
    { id: 'notices', label: 'Notices', icon: <Bell className="w-5 h-5" /> },
    { id: 'reactivations', label: 'Reactivations', icon: <RotateCcw className="w-5 h-5" /> },
    { id: 'addresses', label: 'Addresses', icon: <MapPin className="w-5 h-5" /> },
    { id: 'reports', label: 'Reports', icon: <FileText className="w-5 h-5" /> },
    { id: 'settings', label: 'Settings', icon: <Settings className="w-5 h-5" /> },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <Trash2 className="w-8 h-8 text-[#1B2D5C]" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Admin Portal</h1>
                <p className="text-sm text-gray-500">Riffe Family Sanitation Management System</p>
              </div>
            </div>
            <button onClick={onLogout} className="text-gray-600 hover:text-gray-900 font-medium">
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-1 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-3 font-medium text-sm whitespace-nowrap border-b-2 transition ${
                  activeTab === tab.id
                    ? 'border-[#1B2D5C] text-[#1B2D5C]'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'dashboard' && <Dashboard setActiveTab={setActiveTab} />}
        {activeTab === 'requests' && <ServiceRequestsTab onUpdate={loadData} />}
        {activeTab === 'customers' && <CustomersTab customers={customers} subscriptions={subscriptions} services={services} invoices={invoices} allLocations={allLocations} onUpdate={loadData} />}
        {activeTab === 'services' && <ServicesTab services={services} onUpdate={loadData} />}
        {activeTab === 'notices' && <NoticesTab notices={notices} onUpdate={loadData} />}
        {activeTab === 'reactivations' && <ReactivationsTab />}
        {activeTab === 'addresses' && <AddressReviewTab />}
        {activeTab === 'reports' && <ReportsTab />}
        {activeTab === 'settings' && <SettingsTab onBackup={loadData} />}
      </div>
    </div>
  );
};

// Dashboard Component
const Dashboard = ({ setActiveTab }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/dashboard', { headers: { Authorization: `Bearer ${auth.getToken()}` } })
      .then(r => r.json())
      .then(d => { setStats(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-24">
      <div className="animate-spin rounded-full h-10 w-10 border-4 border-gray-200 border-t-[#1B2D5C]" />
    </div>
  );
  if (!stats) return <p className="text-center text-gray-500 py-12">Failed to load dashboard.</p>;

  const revChange = stats.revenueLastMonth > 0
    ? (((stats.revenueThisMonth - stats.revenueLastMonth) / stats.revenueLastMonth) * 100).toFixed(1)
    : null;

  const activityIcon = (type) => {
    if (type === 'payment') return <span className="text-green-500 font-bold text-base">$</span>;
    if (type === 'signup') return <Users className="w-4 h-4 text-[#1B2D5C]" />;
    if (type === 'reactivation') return <RotateCcw className="w-4 h-4 text-blue-500" />;
  };
  const activityLabel = (a) => {
    if (a.type === 'payment') return `${a.name} paid $${Number(a.amount).toFixed(2)}${a.method ? ' via ' + a.method : ''}`;
    if (a.type === 'signup') return `${a.name} signed up`;
    if (a.type === 'reactivation') return `${a.name} reactivated`;
    return a.name;
  };

  return (
    <div className="space-y-6">

      {/* Dispatch button */}
      <div className="flex justify-end">
        <a
          href="/routing/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1B2D5C] text-white font-semibold rounded-lg shadow hover:bg-[#0f1e3d] transition"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/>
          </svg>
          Open Dispatch
        </a>
      </div>

      {/* Row 1 — Key metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow p-5">
          <p className="text-sm text-gray-500 font-medium">Revenue This Month</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">${Number(stats.revenueThisMonth).toFixed(2)}</p>
          {revChange !== null && (
            <p className={`text-xs mt-1 font-medium ${parseFloat(revChange) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
              {parseFloat(revChange) >= 0 ? '▲' : '▼'} {Math.abs(revChange)}% vs last month
            </p>
          )}
          <p className="text-xs text-gray-400 mt-1">Last month: ${Number(stats.revenueLastMonth).toFixed(2)}</p>
        </div>

        <div className="bg-white rounded-xl shadow p-5">
          <p className="text-sm text-gray-500 font-medium">Outstanding Balance</p>
          <p className="text-3xl font-bold text-yellow-600 mt-1">${Number(stats.outstandingBalance).toFixed(2)}</p>
          <p className="text-xs text-gray-400 mt-1">Total unpaid invoices</p>
        </div>

        <div className="bg-white rounded-xl shadow p-5">
          <p className="text-sm text-gray-500 font-medium">New Customers This Month</p>
          <p className="text-3xl font-bold text-[#1B2D5C] mt-1">{stats.newCustomers.length}</p>
          <p className="text-xs text-gray-400 mt-1">Inactive total: {stats.inactiveCount}</p>
        </div>

        <div
          className="bg-white rounded-xl shadow p-5 cursor-pointer hover:shadow-md transition"
          onClick={() => setActiveTab('reactivations')}
        >
          <p className="text-sm text-gray-500 font-medium">Pending Reactivations</p>
          <p className={`text-3xl font-bold mt-1 ${stats.pendingReactivations > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
            {stats.pendingReactivations}
          </p>
          <p className="text-xs text-[#1B2D5C] mt-1 underline">View reactivations →</p>
        </div>
      </div>

      {/* Row 2 — Overdue */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl shadow p-5 border-l-4 border-yellow-400">
          <div className="flex justify-between items-start mb-3">
            <div>
              <p className="text-sm font-semibold text-yellow-700">Overdue 30+ Days</p>
              <p className="text-2xl font-bold text-gray-900">{stats.overdue30.length} customers</p>
            </div>
            <p className="text-xl font-bold text-yellow-600">${Number(stats.overdue30Total).toFixed(2)}</p>
          </div>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {stats.overdue30.length === 0 && <p className="text-sm text-gray-400">None</p>}
            {stats.overdue30.map(c => (
              <div key={c.customerId} className="flex justify-between items-center text-sm py-1 border-b last:border-0">
                <div>
                  <p className="font-medium text-gray-800">{c.name}</p>
                  <p className="text-xs text-gray-400">Oldest due: {c.oldestDue} &middot; {c.invoiceCount} invoice{c.invoiceCount > 1 ? 's' : ''}</p>
                </div>
                <span className="font-semibold text-yellow-600">${Number(c.totalOwed).toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-5 border-l-4 border-red-500">
          <div className="flex justify-between items-start mb-3">
            <div>
              <p className="text-sm font-semibold text-red-700">Overdue 45+ Days</p>
              <p className="text-2xl font-bold text-gray-900">{stats.overdue45.length} customers</p>
            </div>
            <p className="text-xl font-bold text-red-600">${Number(stats.overdue45Total).toFixed(2)}</p>
          </div>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {stats.overdue45.length === 0 && <p className="text-sm text-gray-400">None</p>}
            {stats.overdue45.map(c => (
              <div key={c.customerId} className="flex justify-between items-center text-sm py-1 border-b last:border-0">
                <div>
                  <p className="font-medium text-gray-800">{c.name}</p>
                  <p className="text-xs text-gray-400">Oldest due: {c.oldestDue} &middot; {c.invoiceCount} invoice{c.invoiceCount > 1 ? 's' : ''}</p>
                </div>
                <span className="font-semibold text-red-600">${Number(c.totalOwed).toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Row 3 — Activity + Panels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
        <div className="bg-white rounded-xl shadow p-5 flex flex-col h-full">
          <h3 className="font-semibold text-gray-900 mb-4">Activity Feed</h3>
          <div className="space-y-3 overflow-y-auto pb-4 flex-1 min-h-0">
            {stats.activity.length === 0 && <p className="text-sm text-gray-400">No recent activity</p>}
            {stats.activity.map((a, i) => (
              <div key={i} className="flex items-start space-x-3 py-2 border-b last:border-0">
                <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  {activityIcon(a.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800">{activityLabel(a)}</p>
                  <p className="text-xs text-gray-400">{new Date(a.date).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow p-5">
            <h3 className="font-semibold text-gray-900 mb-3">Failed Payments</h3>
            <div className="space-y-2 max-h-36 overflow-y-auto">
              {stats.failedPayments.length === 0
                ? <p className="text-sm text-gray-400">No failed payments</p>
                : stats.failedPayments.map(p => (
                    <div key={p.id} className="flex justify-between items-center text-sm py-1 border-b last:border-0">
                      <div>
                        <p className="font-medium text-gray-800">{p.name}</p>
                        <p className="text-xs text-gray-400">{p.createdAt ? new Date(p.createdAt).toLocaleDateString() : ''}</p>
                      </div>
                      <span className="font-semibold text-red-500">${Number(p.amount).toFixed(2)}</span>
                    </div>
                  ))
              }
            </div>
          </div>

          <div className="bg-white rounded-xl shadow p-5">
            <h3 className="font-semibold text-gray-900 mb-3">Went Inactive This Week</h3>
            <div className="space-y-2 max-h-36 overflow-y-auto">
              {stats.inactiveThisWeek.length === 0
                ? <p className="text-sm text-gray-400">None this week</p>
                : stats.inactiveThisWeek.map(c => (
                    <div key={c.id} className="py-1 border-b last:border-0">
                      <p className="text-sm font-medium text-gray-800">{c.name}</p>
                      <p className="text-xs text-gray-400">{c.email} &middot; {c.deactivatedAt ? new Date(c.deactivatedAt).toLocaleDateString() : ''}</p>
                    </div>
                  ))
              }
            </div>
          </div>

          {stats.newCustomers.length > 0 && (
            <div className="bg-white rounded-xl shadow p-5">
              <h3 className="font-semibold text-gray-900 mb-3">New Customers This Month</h3>
              <div className="space-y-2 max-h-36 overflow-y-auto">
                {stats.newCustomers.map(c => (
                  <div key={c.id} className="py-1 border-b last:border-0">
                    <p className="text-sm font-medium text-gray-800">{c.name}</p>
                    <p className="text-xs text-gray-400">{c.email}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

const StatCard = ({ title, value, icon, color }) => (
  <div className="bg-white rounded-xl shadow p-4 md:p-6">
    <div className="flex items-center justify-between mb-2">
      <span className="text-gray-600 text-sm font-medium">{title}</span>
      <div className={`p-2 rounded-lg bg-${color}-100 text-${color}-600`}>{icon}</div>
    </div>
    <p className="text-2xl md:text-3xl font-bold text-gray-900">{value}</p>
  </div>
);

// Service Requests Tab
// ===============================================

const ServiceRequestsTab = ({ onUpdate }) => {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [pendingApprovalRequest, setPendingApprovalRequest] = useState(null);

  useEffect(() => {
    loadRequests();
  }, [filter]);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/service-requests?status=${filter}`);
      const data = await res.json();
      setRequests(data);
    } catch (err) {
      console.error('[LOAD REQUESTS ERROR]', err);
    }
    setLoading(false);
  };

  const handleApprove = async (requestId) => {
    setPendingApprovalRequest(requestId);
  };

  const handleChargeActionSelect = async (chargeAction, pickupDay) => {
    const requestId = pendingApprovalRequest;
    setPendingApprovalRequest(null);

    try {
      const res = await fetch(`/api/service-requests/${requestId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adminId: 'admin', chargeAction, pickupDay: pickupDay || undefined })
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.error || data.details || 'Failed to approve request');

      // Create success message based on charge action
      let actionMsg = '';
      if (chargeAction === 'full') {
        actionMsg = data.totalCharged
          ? `charged $${data.totalCharged.toFixed(2)} (including $${data.processingFee.toFixed(2)} processing fee)`
          : 'invoice created (no payment method on file)';
      } else if (chargeAction === 'partial') {
        actionMsg = data.totalCharged
          ? `charged prorated amount $${data.totalCharged.toFixed(2)} (including $${data.processingFee.toFixed(2)} processing fee)`
          : 'prorated invoice created (no payment method on file)';
      } else {
        actionMsg = `will be billed at full price on ${data.nextBilling || 'the 5th'}`;
      }

      safeShowAlert(`Service request approved - ${actionMsg}`);
      loadRequests();
      onUpdate();
    } catch (err) {
      safeShowAlert('Error: ' + err.message);
    }
  };

  const handleReject = async (requestId) => {
    const reason = window.prompt('Reason for rejection (optional):');
    if (reason === null) return; // Cancelled

    try {
      const res = await fetch(`/api/service-requests/${requestId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adminId: 'admin', reason })
      });

      const data = await res.json();
      
      if (!res.ok) throw new Error(data.error);

      safeShowAlert('Service request rejected');
      loadRequests();
    } catch (err) {
      safeShowAlert('Error: ' + err.message);
    }
  };

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <div className="space-y-6">
      {/* Header with alert badge */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <h2 className="text-2xl font-bold">Service Requests</h2>
          {pendingCount > 0 && (
            <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-semibold animate-pulse">
              {pendingCount} Pending
            </span>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow p-4">
        <div className="flex space-x-2">
          {['pending', 'approved', 'rejected', 'all'].map(status => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition ${
                filter === status
                  ? 'bg-[#1B2D5C] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Requests List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-200 border-t-green-600"></div>
          <p className="text-gray-600 mt-3">Loading requests...</p>
        </div>
      ) : requests.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <Bell className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No {filter !== 'all' ? filter : ''} service requests found.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map(req => (
            <div
              key={req.id}
              className={`bg-white rounded-xl shadow p-6 border-l-4 ${
                req.status === 'pending' ? 'border-yellow-500' :
                req.status === 'approved' ? 'border-green-500' :
                'border-red-500'
              }`}
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{req.serviceName}</h3>
                  <div className="space-y-1">
                    <p className="text-sm text-gray-600">
                      <strong>Customer:</strong> {req.customerName}
                    </p>
                    <p className="text-sm text-gray-600">
                      <Mail className="w-4 h-4 inline mr-1" />
                      {req.customerEmail}
                    </p>
                    {req.customerPhone && (
                      <p className="text-sm text-gray-600">
                        <Phone className="w-4 h-4 inline mr-1" />
                        {req.customerPhone}
                      </p>
                    )}
                    {req.customerAddress && (
                      <p className="text-sm text-gray-600">
                        <MapPin className="w-4 h-4 inline mr-1" />
                        {req.customerAddress}
                        {req.customerCity && `, ${req.customerCity}`}
                        {req.customerZip && ` ${req.customerZip}`}
                      </p>
                    )}
                    <p className="text-sm text-gray-500 mt-2">Requested: {new Date(req.requestDate).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="text-right ml-4">
                  <p className="text-2xl font-bold text-[#1B2D5C]">${req.servicePrice}</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mt-2 ${
                    req.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                    req.status === 'approved' ? 'bg-[#dce4f5] text-[#0f1e3d]' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {req.status}
                  </span>
                </div>
              </div>

              {req.notes && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700"><strong>Notes:</strong> {req.notes}</p>
                </div>
              )}

              {req.reviewedAt && (
                <p className="text-xs text-gray-500 mb-4">
                  Reviewed by {req.reviewedBy} on {new Date(req.reviewedAt).toLocaleString()}
                </p>
              )}

              {req.status === 'pending' && (
                <div className="flex space-x-3 pt-4 border-t">
                  <button
                    onClick={() => handleApprove(req.id)}
                    className="flex-1 bg-[#1B2D5C] text-white px-4 py-2 rounded-lg hover:bg-[#0f1e3d] font-medium"
                  >
                    ✓ Approve & Create Subscription
                  </button>
                  <button
                    onClick={() => handleReject(req.id)}
                    className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 font-medium"
                  >
                    ✗ Reject
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Charge Action Modal */}
      {pendingApprovalRequest && (
        <ChargeActionModal
          requestId={pendingApprovalRequest}
          onClose={() => setPendingApprovalRequest(null)}
          onSelect={handleChargeActionSelect}
        />
      )}
    </div>
  );
};

// Reactivations Tab
// ============================
const ReactivationsTab = () => {
  const [reactivations, setReactivations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadReactivations();
  }, []);

  const loadReactivations = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/reactivations', { headers: { Authorization: `Bearer ${auth.getToken()}` } });
      const data = await res.json();
      setReactivations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const acknowledge = async (id) => {
    try {
      await fetch(`/api/reactivations/${id}/acknowledge`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${auth.getToken()}` }
      });
      setReactivations(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Reactivations</h2>
          <p className="text-gray-500 text-sm mt-1">Customers who paid past-due invoices and had their service resumed</p>
        </div>
        <span className="bg-[#1B2D5C] text-white text-sm font-semibold px-3 py-1 rounded-full">
          {reactivations.length} pending
        </span>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-200 border-t-[#1B2D5C]"></div>
          <p className="text-gray-600 mt-3">Loading reactivations...</p>
        </div>
      ) : reactivations.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <RotateCcw className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No pending reactivations</p>
          <p className="text-gray-400 text-sm mt-1">Customers who pay off past-due balances will appear here</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reactivations.map(r => (
            <div key={r.id} className="bg-white rounded-xl shadow p-6 border-l-4 border-[#1B2D5C]">
              <div className="flex justify-between items-start">
                <div className="flex-1 space-y-1">
                  <h3 className="text-lg font-semibold text-gray-900">{r.name}</h3>
                  <p className="text-sm text-gray-600"><Mail className="w-4 h-4 inline mr-1" />{r.email}</p>
                  {r.phone && <p className="text-sm text-gray-600"><Phone className="w-4 h-4 inline mr-1" />{r.phone}</p>}
                  {r.address && (
                    <p className="text-sm text-gray-600">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      {r.address}{r.city ? `, ${r.city}` : ''}{r.zip ? ` ${r.zip}` : ''}
                    </p>
                  )}
                  {r.pickupDay && (
                    <p className="text-sm text-gray-600">
                      <Calendar className="w-4 h-4 inline mr-1" />
                      <strong>Pickup Day:</strong> {r.pickupDay}
                    </p>
                  )}
                  {r.services && (
                    <p className="text-sm text-gray-600">
                      <Package className="w-4 h-4 inline mr-1" />
                      <strong>Services:</strong> {r.services}
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-2">
                    Reactivated: {new Date(r.reactivatedAt).toLocaleString()}
                  </p>
                </div>
                <div className="ml-6 flex-shrink-0">
                  <button
                    onClick={() => acknowledge(r.id)}
                    className="bg-[#1B2D5C] text-white px-5 py-2 rounded-lg hover:bg-[#0f1e3d] font-medium text-sm transition"
                  >
                    ✓ Acknowledge
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Customers Tab
// ============================
const CustomersTab = ({ customers, subscriptions, services, invoices, allLocations, onUpdate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Build lookups: serviceId -> service, and customerId -> Array of active service objects
  const serviceById = useMemo(() => {
    const m = new Map();
    for (const s of services || []) m.set(s.id, s);
    return m;
  }, [services]);

  const customerServices = useMemo(() => {
    const m = new Map();
    for (const sub of subscriptions || []) {
      if (sub.status !== 'active') continue;
      const svc = serviceById.get(sub.serviceId);
      if (!svc) continue;
      if (!m.has(sub.customerId)) m.set(sub.customerId, []);
      m.get(sub.customerId).push(svc);
    }
    return m;
  }, [subscriptions, serviceById]);

  const sortedServices = useMemo(
    () => [...(services || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [services]
  );

  const filteredCustomers = customers
    .filter((c) => {
      const search = searchTerm.toLowerCase().trim();

      // Skip filtering if search is empty
      if (!search) return true;

      // Search in customer's primary info
      const matchesPrimary = (
        c.name?.toLowerCase().includes(search) ||
        c.email?.toLowerCase().includes(search) ||
        c.phone?.toLowerCase().includes(search) ||
        c.address?.toLowerCase().includes(search) ||
        c.city?.toLowerCase().includes(search) ||
        c.zip?.toLowerCase().includes(search)
      );

      // Search in alternative service locations
      const matchesLocation = allLocations?.some(loc =>
        loc.customerId === c.id && (
          loc.address?.toLowerCase().includes(search) ||
          loc.city?.toLowerCase().includes(search) ||
          loc.zip?.toLowerCase().includes(search) ||
          loc.label?.toLowerCase().includes(search)
        )
      ) || false;

      return matchesPrimary || matchesLocation;
    })
    .filter((c) =>
      statusFilter === 'all' ? true : c.status === statusFilter
    )
    .filter((c) => {
      if (serviceFilter === 'all') return true;
      const svcs = customerServices.get(c.id) || [];
      return svcs.some((s) => s.id === serviceFilter);
    });

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Customers</h2>

        <button
          onClick={() => setShowAddModal(true)}
          className="bg-[#1B2D5C] text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-[#0f1e3d]"
        >
          <Plus className="w-5 h-5" />
          <span>Add Customer</span>
        </button>
      </div>

      {/* FILTER BAR */}
      <div className="bg-white rounded-xl shadow p-4">
        <div className="flex flex-wrap gap-4 items-center">

          {/* Search */}
          <div className="flex-1 min-w-0 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search customers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          {/* Service Filter */}
          <select
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
          >
            <option value="all">All Services</option>
            {sortedServices.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>

          {/* Reset */}
          {(searchTerm || statusFilter !== 'all' || serviceFilter !== 'all') && (
            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setServiceFilter('all');
              }}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 underline"
            >
              Reset
            </button>
          )}
        </div>

        {/* Result count */}
        <div className="mt-3 text-sm text-gray-500">
          Showing {filteredCustomers.length} of {customers.length} customers
        </div>
      </div>

      {/* CUSTOMER LIST TABLE */}
      <div className="bg-white rounded-xl shadow overflow-hidden overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
              <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
              <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">AutoPay</th>
              <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200">
            {filteredCustomers.map((customer) => (
              <tr key={customer.id} className="hover:bg-gray-50">

                {/* Customer Name & Address */}
                <td className="px-3 md:px-6 py-4">
                  <p className="font-medium">{customer.name}</p>
                  <p className="text-sm text-gray-500">{customer.address}</p>
                </td>

                {/* Contact Info */}
                <td className="px-3 md:px-6 py-4 text-sm">
                  <p>{customer.email}</p>
                  <p className="text-gray-500">{customer.phone}</p>
                </td>

                {/* Type */}
                <td className="px-3 md:px-6 py-4">
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                    {customer.type}
                  </span>
                </td>

                {/* Status */}
                <td className="px-3 md:px-6 py-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    customer.status === 'active' ? 'bg-[#dce4f5] text-[#0f1e3d]' : 'bg-gray-100 text-gray-700'
                  }`}>
                    {customer.status}
                  </span>
                </td>

                {/* AutoPay */}
                <td className="px-3 md:px-6 py-4">
                  <button
                    onClick={async () => {
                      const newStatus = !customer.autoPayEnabled;
                      try {
                        const res = await fetch(`/api/customers/${customer.id}/autopay`, {
                          method: 'PATCH',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${auth.getToken()}`
                          },
                          body: JSON.stringify({ autoPayEnabled: newStatus })
                        });

                        if (!res.ok) throw new Error('Failed to update autopay');

                        showAlert(`AutoPay ${newStatus ? 'enabled' : 'disabled'} for ${customer.name}`);
                        onUpdate();
                      } catch (err) {
                        showAlert('Error updating AutoPay setting');
                        console.error('[AUTOPAY TOGGLE ERROR]', err);
                      }
                    }}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                      customer.autoPayEnabled
                        ? 'bg-[#dce4f5] text-[#0f1e3d] hover:bg-[#c3d0ee]'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {customer.autoPayEnabled ? 'Enabled' : 'Disabled'}
                  </button>
                </td>

                {/* ACTIONS */}
                <td className="px-3 md:px-6 py-4 space-x-3">
                  <button
                    onClick={() => setSelectedCustomer(customer)}
                    className="text-[#1B2D5C] hover:text-[#0f1e3d] text-sm"
                  >
                    Manage
                  </button>

                  <button
                    onClick={async () => {
                      if (await showConfirm(`Delete ${customer.name}?`)) {
                        await storage.delete("customers", customer.id);
                        onUpdate();
                      }
                    }}
                    className="text-red-600 hover:text-red-700 text-sm"
                  >
                    Delete
                  </button>
                </td>

              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* MODALS */}
      {showAddModal && (
        <AddCustomerModal
          onClose={() => setShowAddModal(false)}
          onUpdate={onUpdate}
        />
      )}

      {selectedCustomer && (
        <CustomerDetailModal
          customer={selectedCustomer}
          subscriptions={subscriptions.filter((s) => s.customerId === selectedCustomer.id)}
          services={services}
          invoices={invoices.filter((i) => i.customerId === selectedCustomer.id)}
          onClose={() => setSelectedCustomer(null)}
          onUpdate={onUpdate}
        />
      )}

    </div>
  );
};



// Services Tab
const ServicesTab = ({ services, onUpdate }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingService, setEditingService] = useState(null);

  const groupedServices = {
    residential: services.filter(s => s.category === 'residential'),
    commercial: services.filter(s => s.category === 'commercial'),
    dumpster: services.filter(s => s.category === 'dumpster'),
    additional: services.filter(s => s.category === 'additional'),
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Services & Pricing</h2>
        <button onClick={() => setShowAddModal(true)} className="bg-[#1B2D5C] text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-[#0f1e3d]">
          <Plus className="w-5 h-5" />
          <span>Add Service</span>
        </button>
      </div>

      {/* Service Categories */}
      {Object.entries(groupedServices).map(([category, items]) => (
        <div key={category} className="bg-white rounded-xl shadow overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b">
            <h3 className="text-lg font-semibold capitalize">{category} Services</h3>
          </div>
          <div className="divide-y">
            {items.map(service => (
              <div key={service.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{service.name}</h4>
                  <p className="text-sm text-gray-500">{service.description}</p>
                  {service.units !== null && (
                    <p className="text-xs text-blue-600 mt-1">Available Units: {service.units}</p>
                  )}
                </div>
                <div className="flex items-center space-x-4">
                  <span className="text-xl font-bold text-[#1B2D5C]">${service.price}</span>
                  <button onClick={() => setEditingService(service)} className="text-[#1B2D5C] hover:text-[#0f1e3d]">
                    <Edit className="w-5 h-5" />
                  </button>
                  <button
  onClick={async () => {
    if (await showConfirm(`Delete service "${service.name}"?`)) {
      await storage.delete('services', service.id);
      onUpdate();
    }
  }}
  className="text-red-600 hover:text-red-700"
>
  <Trash2 className="w-5 h-5" />
</button>

                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {showAddModal && <AddServiceModal onClose={() => setShowAddModal(false)} onUpdate={onUpdate} />}
      {editingService && <EditServiceModal service={editingService} onClose={() => setEditingService(null)} onUpdate={onUpdate} />}
    </div>
  );
};

// Billing Tab
const BillingTab = ({ invoices, customers, subscriptions, services, onUpdate }) => {
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showStatementModal, setShowStatementModal] = useState(null);
  const [showRecordPaymentModal, setShowRecordPaymentModal] = useState(null);
  const [billingSummary, setBillingSummary] = useState(null);

  useEffect(() => {
    loadBillingSummary();
  }, [invoices]);

  const loadBillingSummary = async () => {
    try {
      const res = await fetch(`/api/billing/summary`);
      const data = await res.json();
      setBillingSummary(data);
    } catch (err) {
      console.error('Failed to load billing summary:', err);
    }
  };

  const filteredInvoices = invoices
    .filter(i => filterStatus === 'all' || i.status === filterStatus)
    .filter(i => {
      if (!searchTerm) return true;
      const customer = customers.find(c => c.id === i.customerId);
      return (
        i.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });

  const runBillingCycle = async () => {
    if (!(await showConfirm('Run billing cycle now? This will generate invoices for all due subscriptions.'))) 
      return;
    const res = await fetch(`/api/invoices/run-billing`, { method: "POST" });
    const data = await res.json();
    safeShowAlert(data.message || "Billing complete");
    onUpdate();
    loadBillingSummary();
  };

  const markAsPaid = async (invoice, method = 'manual', notes = '') => {
    try {
      const res = await fetch(`/api/billing/invoices/${invoice.id}/paid`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ method, notes })
      });
      if (!res.ok) throw new Error('Failed to mark as paid');
      onUpdate();
      loadBillingSummary();
    } catch (err) {
      safeShowAlert('Error: ' + err.message);
    }
  };

  const sendInvoiceEmail = async (invoice) => {
    try {
      const res = await fetch(`/api/billing/invoices/${invoice.id}/send`, { method: 'POST' });
      const data = await res.json();
      safeShowAlert(data.message || 'Invoice sent!');
    } catch (err) {
      safeShowAlert('Failed to send invoice: ' + err.message);
    }
  };

  const downloadPDF = (invoice) => {
    window.open(`/api/billing/invoices/${invoice.id}/pdf`, '_blank');
  };

  const deleteInvoice = async (invoice) => {
    if (!(await showConfirm(`Delete invoice ${invoice.id}? This cannot be undone.`)))
       return;
    try {
      const res = await fetch(`/api/billing/invoices/${invoice.id}`, { method: 'DELETE' });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to delete');
      }
      onUpdate();
      loadBillingSummary();
    } catch (err) {
      safeShowAlert('Error: ' + err.message);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h2 className="text-2xl font-bold">Billing Management</h2>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 w-full sm:w-auto">
          <button onClick={() => setShowCreateModal(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-blue-700">
            <Plus className="w-5 h-5" />
            <span>Create Invoice</span>
          </button>
          <button onClick={runBillingCycle} className="bg-[#1B2D5C] text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-[#0f1e3d]">
            <Calendar className="w-5 h-5" />
            <span>Run Billing Cycle</span>
          </button>
        </div>
      </div>

      {/* Billing Summary Cards */}
      {billingSummary && (
        <div className="grid md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow p-4 border-l-4 border-blue-500">
            <p className="text-sm text-gray-600">Total Invoiced</p>
            <p className="text-2xl font-bold text-gray-900">${billingSummary.totalInvoiced?.toFixed(2) || '0.00'}</p>
            <p className="text-xs text-gray-500">{billingSummary.invoiceCount || 0} invoices</p>
          </div>
          <div className="bg-white rounded-xl shadow p-4 border-l-4 border-green-500">
            <p className="text-sm text-gray-600">Total Paid</p>
            <p className="text-2xl font-bold text-[#1B2D5C]">${billingSummary.totalPaid?.toFixed(2) || '0.00'}</p>
            <p className="text-xs text-gray-500">{billingSummary.paidCount || 0} paid</p>
          </div>
          <div className="bg-white rounded-xl shadow p-4 border-l-4 border-yellow-500">
            <p className="text-sm text-gray-600">Unpaid</p>
            <p className="text-2xl font-bold text-yellow-600">${billingSummary.totalPending?.toFixed(2) || '0.00'}</p>
            <p className="text-xs text-gray-500">{billingSummary.pendingCount || 0} unpaid</p>
          </div>
          <div className="bg-white rounded-xl shadow p-4 border-l-4 border-red-500">
            <p className="text-sm text-gray-600">Overdue</p>
            <p className="text-2xl font-bold text-red-600">${billingSummary.totalOverdue?.toFixed(2) || '0.00'}</p>
            <p className="text-xs text-gray-500">{billingSummary.overdueCount || 0} overdue</p>
          </div>
        </div>
      )}

      {/* Filters & Search */}
      <div className="bg-white rounded-xl shadow p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
          >
            <option value="all">All Invoices</option>
            <option value="pending">Unpaid</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
          </select>
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search invoices or customers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Invoice List */}
      <div className="bg-white rounded-xl shadow overflow-hidden overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
              <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
              <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
              <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
              <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">AutoPay</th>
              <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredInvoices.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-8 text-center text-gray-500">No invoices found</td>
              </tr>
            ) : filteredInvoices.map(invoice => {
              const customer = customers.find(c => c.id === invoice.customerId);
              const isOverdue = invoice.status === 'pending' && invoice.dueDate < new Date().toISOString().split('T')[0];
              return (
                <tr key={invoice.id} className="hover:bg-gray-50">
                  <td className="px-3 md:px-6 py-4 text-sm font-medium text-gray-900">{invoice.id}</td>
                  <td className="px-3 md:px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{customer?.name || 'Unknown'}</p>
                      <button
                        onClick={() => setShowStatementModal(customer)}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        View Statement
                      </button>
                    </div>
                  </td>
                  <td className="px-3 md:px-6 py-4 text-sm text-gray-500">{invoice.date}</td>
                  <td className="px-3 md:px-6 py-4 text-sm text-gray-500">{invoice.dueDate}</td>
                  <td className="px-3 md:px-6 py-4 text-sm font-semibold text-gray-900">${(invoice.amount || 0).toFixed(2)}</td>
                  <td className="px-3 md:px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      invoice.status === 'paid' ? 'bg-[#dce4f5] text-[#0f1e3d]' :
                      isOverdue ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {isOverdue ? 'Overdue' : invoiceStatusLabel(invoice.status)}
                    </span>
                  </td>
                  <td className="px-3 md:px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button onClick={() => setSelectedInvoice(invoice)} className="p-2 text-gray-600 hover:text-[#1B2D5C]" title="View Details">
                        <FileText className="w-4 h-4" />
                      </button>
                      <button onClick={() => downloadPDF(invoice)} className="p-2 text-gray-600 hover:text-blue-600" title="Download PDF">
                        <Download className="w-4 h-4" />
                      </button>
                      <button onClick={() => sendInvoiceEmail(invoice)} className="p-2 text-gray-600 hover:text-purple-600" title="Send Email">
                        <Mail className="w-4 h-4" />
                      </button>
                      {invoice.status !== 'paid' && (
                        <>
                          <button onClick={() => setShowRecordPaymentModal(invoice)} className="p-2 text-gray-600 hover:text-[#1B2D5C]" title="Record Payment">
                            <DollarSign className="w-4 h-4" />
                          </button>
                          <button onClick={() => deleteInvoice(invoice)} className="p-2 text-gray-600 hover:text-red-600" title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Invoice Detail Modal */}
      {selectedInvoice && (
        <InvoiceDetailModal
          invoice={selectedInvoice}
          customer={customers.find(c => c.id === selectedInvoice.customerId)}
          onClose={() => setSelectedInvoice(null)}
          onUpdate={() => { onUpdate(); loadBillingSummary(); }}
          onMarkPaid={markAsPaid}
          onSendEmail={sendInvoiceEmail}
          onDownloadPDF={downloadPDF}
        />
      )}

      {/* Create Invoice Modal */}
      {showCreateModal && (
        <CreateInvoiceModal
          customers={customers}
          services={services}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => { onUpdate(); loadBillingSummary(); setShowCreateModal(false); }}
        />
      )}

      {/* Customer Statement Modal */}
      {showStatementModal && (
        <CustomerStatementModal
          customer={showStatementModal}
          onClose={() => setShowStatementModal(null)}
        />
      )}

      {/* Record Payment Modal */}
      {showRecordPaymentModal && (
        <RecordPaymentModal
          invoice={showRecordPaymentModal}
          customer={customers.find(c => c.id === showRecordPaymentModal.customerId)}
          onClose={() => setShowRecordPaymentModal(null)}
          onSuccess={() => {
            onUpdate();
            loadBillingSummary();
            setShowRecordPaymentModal(null);
          }}
        />
      )}
    </div>
  );
};

/* =========================================================
   CREATE INVOICE MODAL
   ========================================================= */
const CreateInvoiceModal = ({ customers, services, onClose, onCreated, preselectedCustomerId }) => {
  const [customerId, setCustomerId] = useState(preselectedCustomerId || '');
  const [items, setItems] = useState([{ service: '', amount: '', description: '' }]);
  const [dueDate, setDueDate] = useState('');
  const [saving, setSaving] = useState(false);

  const addItem = () => setItems([...items, { service: '', amount: '', description: '' }]);
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  const updateItem = (idx, field, value) => {
    const updated = [...items];
    updated[idx][field] = value;
    if (field === 'service' && value) {
      const svc = services.find(s => s.id === value);
      if (svc) {
        updated[idx].amount = svc.price;
        updated[idx].description = svc.name;
      }
    }
    setItems(updated);
  };

  const total = items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!customerId || items.length === 0 || !items.some(i => i.amount)) {
      safeShowAlert('Please select a customer and add at least one item');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`/api/billing/invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId,
          items: items.filter(i => i.amount).map(i => ({
            service: i.description || 'Service',
            amount: parseFloat(i.amount)
          })),
          dueDate: dueDate || undefined
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to create invoice');
      }

      const data = await res.json();
      safeShowAlert(`Invoice ${data.invoice.id} created successfully!`);
      onCreated();
    } catch (err) {
      safeShowAlert('Error: ' + err.message);
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">Create Invoice</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Customer Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Customer *</label>
            <select
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
              required
              disabled={preselectedCustomerId}
            >
              <option value="">Select a customer...</option>
              {customers.map(c => (
                <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
              ))}
            </select>
          </div>

          {/* Due Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Due Date</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
            />
            <p className="text-xs text-gray-500 mt-1">Leave blank for default (15 days)</p>
          </div>

          {/* Line Items */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Line Items *</label>
            <div className="space-y-3">
              {items.map((item, idx) => (
                <div key={idx} className="flex gap-3 items-start">
                  <select
                    value={item.service}
                    onChange={(e) => updateItem(idx, 'service', e.target.value)}
                    className="flex-1 px-3 py-2 border rounded-lg text-sm"
                  >
                    <option value="">Select service or custom...</option>
                    {services.map(s => (
                      <option key={s.id} value={s.id}>{s.name} - ${s.price}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="Description"
                    value={item.description}
                    onChange={(e) => updateItem(idx, 'description', e.target.value)}
                    className="flex-1 px-3 py-2 border rounded-lg text-sm"
                  />
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Amount"
                    value={item.amount}
                    onChange={(e) => updateItem(idx, 'amount', e.target.value)}
                    className="w-24 px-3 py-2 border rounded-lg text-sm"
                  />
                  {items.length > 1 && (
                    <button type="button" onClick={() => removeItem(idx)} className="p-2 text-red-600 hover:bg-red-50 rounded">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button type="button" onClick={addItem} className="mt-3 text-sm text-blue-600 hover:underline flex items-center space-x-1">
              <Plus className="w-4 h-4" />
              <span>Add line item</span>
            </button>
          </div>

          {/* Total */}
          <div className="border-t pt-4">
            <div className="flex justify-between text-lg font-bold">
              <span>Total:</span>
              <span>${total.toFixed(2)}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="px-6 py-2 bg-[#1B2D5C] text-white rounded-lg hover:bg-[#0f1e3d] disabled:opacity-50">
              {saving ? 'Creating...' : 'Create Invoice'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* =========================================================
   CUSTOMER STATEMENT MODAL
   ========================================================= */
const CustomerStatementModal = ({ customer, onClose }) => {
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (customer?.id) {
      loadStatement();
    }
  }, [customer]);

  const loadStatement = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/billing/customers/${customer.id}/statement`);
      const data = await res.json();
      setStatement(data);
    } catch (err) {
      console.error('Failed to load statement:', err);
    }
    setLoading(false);
  };

  if (!customer) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-3xl w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">Customer Statement</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {loading ? (
          <div className="py-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-200 border-t-green-600"></div>
            <p className="text-gray-600 mt-3">Loading statement...</p>
          </div>
        ) : statement ? (
          <div className="space-y-6">
            {/* Customer Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-lg">{statement.customer.name}</h3>
              <p className="text-sm text-gray-600">{statement.customer.email}</p>
              {statement.customer.phone && <p className="text-sm text-gray-600">{statement.customer.phone}</p>}
              {statement.customer.address && (
                <p className="text-sm text-gray-600">
                  {statement.customer.address}, {statement.customer.city} {statement.customer.zip}
                </p>
              )}
            </div>

            {/* Summary */}
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-600">Total Invoiced</p>
                <p className="text-2xl font-bold text-blue-600">${statement.summary.totalInvoiced.toFixed(2)}</p>
              </div>
              <div className="bg-[#eef1fa] rounded-lg p-4 text-center">
                <p className="text-sm text-gray-600">Total Paid</p>
                <p className="text-2xl font-bold text-[#1B2D5C]">${statement.summary.totalPaid.toFixed(2)}</p>
              </div>
              <div className={`rounded-lg p-4 text-center ${statement.summary.balance > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
                <p className="text-sm text-gray-600">Balance Due</p>
                <p className={`text-2xl font-bold ${statement.summary.balance > 0 ? 'text-red-600' : 'text-gray-600'}`}>
                  ${statement.summary.balance.toFixed(2)}
                </p>
              </div>
            </div>

            {statement.summary.overdueCount > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  {statement.summary.overdueCount} overdue invoice(s) totaling ${statement.summary.overdueAmount.toFixed(2)}
                </p>
              </div>
            )}

            {/* Invoice History */}
            <div>
              <h3 className="font-semibold mb-3">Invoice History</h3>
              <div className="border rounded-lg overflow-hidden overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 md:px-4 py-2 text-left">Invoice</th>
                      <th className="px-3 md:px-4 py-2 text-left">Date</th>
                      <th className="px-3 md:px-4 py-2 text-left">Due</th>
                      <th className="px-3 md:px-4 py-2 text-right">Amount</th>
                      <th className="px-3 md:px-4 py-2 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {statement.invoices.map(inv => (
                      <tr key={inv.id} className="hover:bg-gray-50">
                        <td className="px-3 md:px-4 py-2 font-medium">{inv.id}</td>
                        <td className="px-3 md:px-4 py-2 text-gray-600">{inv.date}</td>
                        <td className="px-3 md:px-4 py-2 text-gray-600">{inv.dueDate}</td>
                        <td className="px-3 md:px-4 py-2 text-right font-medium">${(inv.amount || 0).toFixed(2)}</td>
                        <td className="px-3 md:px-4 py-2 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            inv.status === 'paid' ? 'bg-[#dce4f5] text-[#0f1e3d]' :
                            inv.dueDate < new Date().toISOString().split('T')[0] ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {inv.status === 'pending' && inv.dueDate < new Date().toISOString().split('T')[0] ? 'Overdue' : invoiceStatusLabel(inv.status)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-gray-600">Failed to load statement</p>
        )}

        <div className="flex justify-end mt-6">
          <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Promos Tab
// =========================
// Promos Tab (with Delete)
// =========================
const PromosTab = ({ promos, onUpdate }) => {
  const [showAddModal, setShowAddModal] = useState(false);

  const togglePromo = async (promo) => {
  await fetch(`/api/promos/${promo.id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ active: !promo.active })
  });

  onUpdate();
};

const deletePromo = async (promo) => {
  if (!(await showConfirm(`Delete promo "${promo.code}"?`)))
     return;

  await fetch(`/api/promos/${promo.id}`, {
    method: "DELETE"
  });

  onUpdate();
};


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Promotions & Discounts</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-[#1B2D5C] text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-[#0f1e3d]"
        >
          <Plus className="w-5 h-5" />
          <span>Add Promotion</span>
        </button>
      </div>

      {/* Promo Cards */}
      <div className="grid md:grid-cols-2 gap-6">
        {promos.map((promo, i) => (
          <div
            key={promo.id || `promo-${i}`}
            className="bg-white rounded-xl shadow p-6"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {promo.code}
                </h3>
                <p className="text-sm text-gray-600">{promo.description}</p>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => togglePromo(promo)}
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    promo.active
                      ? "bg-[#dce4f5] text-[#0f1e3d]"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {promo.active ? "Active" : "Inactive"}
                </button>
                <button
                  onClick={() => deletePromo(promo)}
                  className="text-red-600 hover:text-red-700"
                  title="Delete Promo"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Discount:</span>
                <span className="font-semibold">
                  {promo.type === "percent"
                    ? `${promo.discount}%`
                    : `$${promo.discount}`}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Expires:</span>
                <span className="font-semibold">{promo.expires}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add Promo Modal */}
      {showAddModal && (
        <AddPromoModal
          onClose={() => setShowAddModal(false)}
          onUpdate={onUpdate}
        />
      )}
    </div>
  );
};

// Notices Tab
// =========================
// Notices Tab (with Delete)
// =========================
const NoticesTab = ({ notices, onUpdate }) => {
  const [showAddModal, setShowAddModal] = useState(false);

  const toggleNotice = async (notice) => {
  await fetch(`/api/notices/${notice.id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ active: !notice.active })
  });

  onUpdate();
};

const deleteNotice = async (notice) => {
  if (!(await showConfirm(`Delete notice "${notice.title}"?`)))
     return;

  await fetch(`/api/notices/${notice.id}`, {
    method: "DELETE"
  });

  onUpdate();
};


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Service Notices</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-[#1B2D5C] text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-[#0f1e3d]"
        >
          <Plus className="w-5 h-5" />
          <span>Add Notice</span>
        </button>
      </div>

      {/* Notice List */}
      <div className="space-y-4">
        {notices.map((notice, i) => (
          <div
            key={notice.id || `notice-${i}`}
            className={`bg-white rounded-xl shadow p-6 border-l-4 ${
              notice.priority === "high"
                ? "border-red-500"
                : notice.priority === "medium"
                ? "border-yellow-500"
                : "border-blue-500"
            }`}
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {notice.title}
                  </h3>
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium ${
                      notice.priority === "high"
                        ? "bg-red-100 text-red-700"
                        : notice.priority === "medium"
                        ? "bg-yellow-100 text-yellow-700"
                        : "bg-blue-100 text-blue-700"
                    }`}
                  >
                    {notice.priority}
                  </span>
                </div>
                <p className="text-gray-600 mb-2">{notice.message}</p>
                <p className="text-sm text-gray-500">Posted: {notice.date}</p>
              </div>

              <div className="flex items-center space-x-2 ml-4">
                <button
                  onClick={() => toggleNotice(notice)}
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    notice.active
                      ? "bg-[#dce4f5] text-[#0f1e3d]"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {notice.active ? "Active" : "Inactive"}
                </button>
                <button
                  onClick={() => deleteNotice(notice)}
                  className="text-red-600 hover:text-red-700"
                  title="Delete Notice"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add Notice Modal */}
      {showAddModal && (
        <AddNoticeModal
          onClose={() => setShowAddModal(false)}
          onUpdate={onUpdate}
        />
      )}
    </div>
  );
};

// Settings Tab
// =========================
const SettingsTab = () => {
  const [cardFee, setCardFee] = useState('0');
  const [achFee, setAchFee] = useState('0');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await fetch(`/api/settings`);
      const settings = await res.json();
      setCardFee(settings.card_processing_fee || '0');
      setAchFee(settings.ach_processing_fee || '0');
    } catch (err) {
      console.error('[SETTINGS LOAD ERROR]', err);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await fetch(`/api/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify({
          card_processing_fee: cardFee,
          ach_processing_fee: achFee
        })
      });

      if (res.ok) {
        safeShowAlert('Processing fee settings saved successfully!');
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (err) {
      console.error('[SETTINGS SAVE ERROR]', err);
      safeShowAlert('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const exportData = async () => {
    const data = {
      services: await storage.get('services'),
      customers: await storage.get('customers'),
      subscriptions: await storage.get('subscriptions'),
      invoices: await storage.get('invoices'),
      notices: await storage.get('notices'),
      exportDate: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `riffe-sanitation-backup-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
  };

  return (
    <div className="space-y-6">
      {/* Processing Fees */}
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
          <CreditCard className="w-5 h-5" />
          <span>Processing Fees</span>
        </h3>
        <p className="text-sm text-gray-600 mb-6">
          Set processing fee percentages to pass on to customers. These fees are added on top of the invoice amount.
        </p>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Card Processing Fee (%)
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={cardFee}
                onChange={(e) => setCardFee(e.target.value)}
                className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="0.00"
              />
              <span className="text-gray-500">%</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Applied to all card payments (Visa, Mastercard, etc.)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ACH Processing Fee (%)
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={achFee}
                onChange={(e) => setAchFee(e.target.value)}
                className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="0.00"
              />
              <span className="text-gray-500">%</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Applied to all ACH/bank account payments
            </p>
          </div>
        </div>

        <button
          onClick={saveSettings}
          disabled={saving}
          className="bg-[#1B2D5C] text-white px-6 py-2 rounded-lg hover:bg-[#0f1e3d] disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Processing Fees'}
        </button>
      </div>

      {/* Backup & Restore */}
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Backup & Export</h3>
        <p className="text-sm text-gray-600 mb-4">
          Download a complete backup of all your data including customers, services, invoices, and subscriptions.
        </p>
        <button onClick={exportData} className="bg-[#1B2D5C] text-white px-4 py-2 rounded-lg hover:bg-[#0f1e3d] flex items-center space-x-2">
          <Download className="w-5 h-5" />
          <span>Export All Data</span>
        </button>
      </div>

      {/* System Info */}

    </div>
  );
};


// Customer Portal
// PATCHED CustomerPortal with debugging
// Replace the CustomerPortal component in your App.jsx with this

// PATCHED CustomerPortal with debugging
// ============================
// Onboarding Wizard
// Forces new customers through service selection + payment method
// before letting them into CustomerPortal.
// ============================
const OnboardingWizard = ({ customer, services, onComplete }) => {
  const hasPayment = !!customer?.stripePaymentMethodId;
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);
  const [paymentComplete, setPaymentComplete] = useState(hasPayment);
  const [submitting, setSubmitting] = useState(false);

  const groupedServices = {
    residential: (services || []).filter(s => s.category === 'residential'),
    commercial:  (services || []).filter(s => s.category === 'commercial'),
    dumpster:    (services || []).filter(s => s.category === 'dumpster'),
    additional:  (services || []).filter(s => s.category === 'additional'),
  };

  const toggleService = (id) =>
    setSelectedServiceIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );

  const handleStep1Continue = () => {
    // If payment method already exists (partial-completion resume), skip step 2
    setCurrentStep(paymentComplete ? 3 : 2);
  };

  const handleFinalSubmit = async () => {
    setSubmitting(true);
    try {
      for (const serviceId of selectedServiceIds) {
        const res = await fetch('/api/service-requests', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth.getToken()}`,
          },
          body: JSON.stringify({
            customerId: customer.id,
            serviceId,
            notes: 'Submitted via onboarding wizard',
          }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || 'Failed to submit service request');
        }
      }
      safeShowAlert('Your requests are submitted and awaiting approval!');
      onComplete();
    } catch (err) {
      safeShowAlert('Error: ' + err.message);
      setSubmitting(false);
    }
  };

  // Progress indicator pills
  const Progress = () => (
    <div className="flex items-center justify-center gap-2 mb-8">
      {[1, 2, 3].map(n => (
        <div
          key={n}
          className={`h-2 w-16 rounded-full transition ${
            n <= currentStep ? 'bg-[#1B2D5C]' : 'bg-gray-200'
          }`}
        />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow p-8">
          <h1 className="text-3xl font-bold mb-2">Welcome to Riffe Family Sanitation</h1>
          <p className="text-gray-600 mb-6">
            Let's get your account set up — just a few quick steps.
          </p>
          <Progress />

          {/* STEP 1 — Service selection */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-semibold">Step 1: Choose your services</h2>
                <p className="text-gray-600 mt-1">
                  Select one or more services you'd like. You can request more anytime.
                </p>
              </div>

              {Object.entries(groupedServices).map(([category, items]) =>
                items.length > 0 ? (
                  <div key={category} className="space-y-4">
                    <h3 className="text-xl font-semibold capitalize text-gray-800">
                      {category === 'dumpster' ? 'Commercial' : category} Services
                    </h3>
                    <div className="grid md:grid-cols-3 gap-6">
                      {items.map(service => {
                        const selected = selectedServiceIds.includes(service.id);
                        return (
                          <div
                            key={service.id}
                            onClick={() => toggleService(service.id)}
                            className={`bg-white rounded-xl shadow p-6 cursor-pointer transition border-2 ${
                              selected
                                ? 'border-[#1B2D5C] ring-2 ring-green-200'
                                : 'border-transparent hover:border-gray-200'
                            }`}
                          >
                            <div className="flex justify-between items-start mb-3">
                              <h4 className="font-semibold text-gray-900">{service.name}</h4>
                              {selected && <Check className="w-6 h-6 text-[#1B2D5C]" />}
                            </div>
                            <p className="text-sm text-gray-600 mb-4">{service.description}</p>
                            <div className="pt-3 border-t">
                              <span className="text-2xl font-bold text-[#1B2D5C]">${service.price}</span>
                              <span className="text-sm text-gray-500 ml-1">
                                {category === 'dumpster' ? 'rental' : '/month'}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null
              )}

              <button
                disabled={selectedServiceIds.length === 0}
                onClick={handleStep1Continue}
                className="w-full bg-[#1B2D5C] text-white py-3 rounded-lg font-semibold hover:bg-[#0f1e3d] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue ({selectedServiceIds.length} selected)
              </button>
            </div>
          )}

          {/* STEP 2 — Payment method */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-semibold">Step 2: Add a payment method</h2>
                <p className="text-gray-600 mt-1">
                  We need a card on file before submitting your service request.
                  You won't be charged until a request is approved.
                </p>
              </div>
              <Elements stripe={stripePromise}>
                <PaymentMethodForm
                  customerId={customer.id}
                  onSuccess={() => {
                    setPaymentComplete(true);
                    setCurrentStep(3);
                  }}
                />
              </Elements>
              {!paymentComplete && (
                <button
                  onClick={() => setCurrentStep(1)}
                  className="text-gray-600 hover:text-gray-900 underline text-sm"
                >
                  ← Back to services
                </button>
              )}
            </div>
          )}

          {/* STEP 3 — Confirm & submit */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-semibold">Step 3: Confirm & submit</h2>
                <p className="text-gray-600 mt-1">
                  You're about to request {selectedServiceIds.length} service
                  {selectedServiceIds.length === 1 ? '' : 's'}. Our team will review and
                  approve shortly.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="font-semibold mb-3">Your selections:</h3>
                <ul className="space-y-2">
                  {selectedServiceIds.map(id => {
                    const s = services.find(x => x.id === id);
                    if (!s) return null;
                    return (
                      <li key={id} className="flex justify-between">
                        <span>{s.name}</span>
                        <span className="font-medium text-[#1B2D5C]">
                          ${s.price}
                          <span className="text-sm text-gray-500 ml-1">
                            {s.category === 'dumpster' ? 'rental' : '/month'}
                          </span>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <button
                disabled={submitting || selectedServiceIds.length === 0}
                onClick={handleFinalSubmit}
                className="w-full bg-[#1B2D5C] text-white py-3 rounded-lg font-semibold hover:bg-[#0f1e3d] disabled:opacity-50"
              >
                {submitting ? 'Submitting...' : 'Submit & enter dashboard'}
              </button>
              {!submitting && (
                <button
                  onClick={() => setCurrentStep(1)}
                  className="text-gray-600 hover:text-gray-900 underline text-sm block"
                >
                  ← Back to services
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// Replace the CustomerPortal component in your App.jsx with this

const CustomerPortal = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [services, setServices] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [notices, setNotices] = useState([]);
  const [credit, setCredit] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [noticesExpanded, setNoticesExpanded] = useState(false);
  // Onboarding gate state
  const [customer, setCustomer] = useState(null);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [gateLoading, setGateLoading] = useState(true);

  // -------------------------------
  // GET CURRENT CUSTOMER FROM JWT
  // -------------------------------
  const getCustomerId = () => {
    try {
      const token = auth.getToken();
      if (!token) return null;
      const payload = JSON.parse(atob(token.split(".")[1]));
      console.log("[JWT DECODE] Customer ID from token:", payload.id);
      return payload.id;
    } catch (err) {
      console.error("[JWT DECODE ERROR]", err);
      return null;
    }
  };

  const customerId = getCustomerId();

  useEffect(() => {
    loadData();
  }, []);

  // -------------------------------
  // MAIN DATA LOADER (WITH DEBUGGING)
  // -------------------------------
  const loadData = async () => {
    if (!customerId) {
      console.warn("[CUSTOMER PORTAL] No customerId found - user may not be logged in");
      return;
    }

    console.log("[CUSTOMER PORTAL] Loading data for customer:", customerId);

    // 1. Load services
    const svc = await storage.get("services");
    setServices(svc || []);
    console.log("[CUSTOMER PORTAL] Services loaded:", svc?.length);

    // 2. Load subscriptions (LOCAL + QBO synced)
    const subs = await fetch(
      `/api/subscriptions?customer=${customerId}`,
      { headers: { Authorization: `Bearer ${auth.getToken()}` } }
    )
      .then((r) => {
        console.log("[CUSTOMER PORTAL] Subscriptions response status:", r.status);
        return r.json();
      })
      .catch((err) => {
        console.error("[CUSTOMER PORTAL] Subscriptions error:", err);
        return [];
      });
    setSubscriptions(subs || []);
    console.log("[CUSTOMER PORTAL] Subscriptions loaded:", subs?.length, subs);

    // 3. Load invoices (LOCAL + QBO synced)
    const inv = await fetch(
      `/api/customers/${customerId}/invoices`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth.getToken()}`
        }
      }
    )
      .then((r) => {
        console.log("[CUSTOMER PORTAL] Invoices response status:", r.status);
        return r.json();
      })
      .catch((err) => {
        console.error("[CUSTOMER PORTAL] Invoices error:", err);
        return [];
      });
    setInvoices(inv || []);
    console.log("[CUSTOMER PORTAL] Invoices loaded:", inv?.length, inv);

    // 4. Load notices
    const allNotices = await storage.get("notices");
    setNotices((allNotices || []).filter((n) => n.active));

    // 5. Load customer credit balance
    const creditData = await fetch(
      `/api/customers/${customerId}/credit`,
      { headers: { Authorization: `Bearer ${auth.getToken()}` } }
    )
      .then((r) => r.json())
      .catch((err) => {
        console.error("[CUSTOMER PORTAL] Credit error:", err);
        return { credit: 0 };
      });
    setCredit(creditData.credit || 0);
    console.log("[CUSTOMER PORTAL] Credit loaded:", creditData.credit);

    // 6. Load customer object + service requests for onboarding gate
    try {
      const [custRes, reqRes] = await Promise.all([
        fetch(`/api/customers/${customerId}`, {
          headers: { Authorization: `Bearer ${auth.getToken()}` },
        }),
        fetch(`/api/service-requests?customerId=${customerId}`, {
          headers: { Authorization: `Bearer ${auth.getToken()}` },
        }),
      ]);
      const custData = await custRes.json().catch(() => null);
      const reqData  = await reqRes.json().catch(() => []);
      setCustomer(custData);
      setServiceRequests(Array.isArray(reqData) ? reqData : []);
    } catch (err) {
      console.error("[CUSTOMER PORTAL] Gate fetch error:", err);
    } finally {
      setGateLoading(false);
    }
  };

  // ------------------------
  // ONBOARDING GATE
  // ------------------------
  if (gateLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-600">
        Loading your account...
      </div>
    );
  }
  // Onboarding is needed only if:
  //   - no payment method on file, OR
  //   - customer has NO subscriptions AND NO service requests
  // Existing customers with active services should skip the wizard entirely.
  const hasAnyService =
    (subscriptions?.length || 0) > 0 || (serviceRequests?.length || 0) > 0;
  const needsOnboarding = !customer?.stripePaymentMethodId && !hasAnyService;
  if (needsOnboarding) {
    return (
      <OnboardingWizard
        customer={customer}
        services={services}
        onComplete={loadData}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* HEADER */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <Trash2 className="w-8 h-8 text-[#1B2D5C]" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">My Account</h1>
              <p className="text-xs text-gray-500">Customer ID: {customerId || "Not logged in"}</p>
            </div>
          </div>
          <button
            onClick={onLogout}
            className="text-gray-600 hover:text-gray-900 font-medium"
          >
            Logout
          </button>
        </div>
      </header>

      {/* TABS */}
      {(() => {
        const tabs = [
          { id: "dashboard", label: "Dashboard", icon: <Home className="w-5 h-5" /> },
          { id: "services", label: "My Services", icon: <Package className="w-5 h-5" /> },
          { id: "billing", label: "Billing", icon: <CreditCard className="w-5 h-5" /> },
          { id: "newservice", label: "Add Service", icon: <Plus className="w-5 h-5" /> },
          { id: "account", label: "Account Settings", icon: <User className="w-5 h-5" /> },
        ];
        const activeLabel = tabs.find(t => t.id === activeTab)?.label || "Menu";
        return (
          <div className="bg-white border-b">
            <div className="max-w-7xl mx-auto px-4">
              {/* Mobile hamburger */}
              <div className="md:hidden">
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="flex items-center justify-between w-full py-3 text-sm font-medium text-gray-700"
                >
                  <span className="flex items-center space-x-2">
                    {tabs.find(t => t.id === activeTab)?.icon}
                    <span>{activeLabel}</span>
                  </span>
                  {mobileMenuOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                {mobileMenuOpen && (
                  <div className="border-t pb-2">
                    {tabs.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => { setActiveTab(tab.id); setMobileMenuOpen(false); }}
                        className={`flex items-center space-x-2 w-full px-3 py-2.5 text-sm font-medium rounded-lg my-0.5 ${
                          activeTab === tab.id
                            ? "bg-[#eef1fa] text-[#1B2D5C]"
                            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                        }`}
                      >
                        {tab.icon}
                        <span>{tab.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {/* Desktop tabs */}
              <nav className="hidden md:flex space-x-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 px-4 py-3 font-medium text-sm whitespace-nowrap border-b-2 ${
                      activeTab === tab.id
                        ? "border-[#1B2D5C] text-[#1B2D5C]"
                        : "border-transparent text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    {tab.icon}
                    <span>{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>
        );
      })()}

      {/* NOTICES */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        {notices.length > 0 && (
          <div className="space-y-3 mb-6">
            {(() => {
              const renderNotice = (n) => (
                <div
                  key={n.id}
                  className={`p-4 border-l-4 rounded-lg ${
                    n.priority === "high"
                      ? "bg-red-50 border-red-500"
                      : n.priority === "medium"
                      ? "bg-yellow-50 border-yellow-500"
                      : "bg-blue-50 border-blue-500"
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <Bell className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold">{n.title}</h4>
                      <p className="text-sm">{n.message}</p>
                    </div>
                  </div>
                </div>
              );

              return (
                <>
                  {/* Desktop: show all notices */}
                  <div className="hidden md:block space-y-3">
                    {notices.map(renderNotice)}
                  </div>
                  {/* Mobile: collapse if more than 1 */}
                  <div className="md:hidden space-y-3">
                    {renderNotice(notices[0])}
                    {notices.length > 1 && !noticesExpanded && (
                      <button
                        onClick={() => setNoticesExpanded(true)}
                        className="flex items-center space-x-1 text-sm font-medium text-gray-600 hover:text-gray-900 w-full justify-center py-1"
                      >
                        <ChevronDown className="w-4 h-4" />
                        <span>Show {notices.length - 1} more notice{notices.length - 1 > 1 ? 's' : ''}</span>
                      </button>
                    )}
                    {notices.length > 1 && noticesExpanded && (
                      <>
                        {notices.slice(1).map(renderNotice)}
                        <button
                          onClick={() => setNoticesExpanded(false)}
                          className="flex items-center space-x-1 text-sm font-medium text-gray-600 hover:text-gray-900 w-full justify-center py-1"
                        >
                          <ChevronUp className="w-4 h-4" />
                          <span>Show less</span>
                        </button>
                      </>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* ACTIVE TAB CONTENT */}
        {activeTab === "dashboard" && (
          <CustomerDashboard
            subscriptions={subscriptions}
            invoices={invoices}
            services={services}
            credit={credit}
          />
        )}

        {activeTab === "services" && (
          <CustomerServices
            subscriptions={subscriptions}
            services={services}
            onUpdate={loadData}
          />
        )}

        {activeTab === "billing" && (
          <CustomerBilling invoices={invoices} customerId={customerId} credit={credit} onCreditUpdate={loadData} />
        )}

        {activeTab === "newservice" && (
          <NewServiceRequest
            services={services}
            customerId={customerId}
            onUpdate={loadData}
          />
        )}

        {activeTab === "account" && (
          <CustomerAccountSettings
            customerId={customerId}
            onUpdate={loadData}
          />
        )}
      </div>
    </div>
  );
};

// CustomerDashboard with Debug Panel
const CustomerDashboard = ({ subscriptions, invoices, services, credit = 0 }) => {
  const [showDebug, setShowDebug] = useState(false);

  const activeSubs = subscriptions.filter(s => s.status === "active");

  // FIX: Calculate totals from ALL invoices, not just subscriptions
  const totalPending = invoices
    .filter(i => i.status === "pending" || i.status === "overdue")
    .reduce((sum, i) => sum + i.amount, 0);

  const totalPaid = invoices
    .filter(i => i.status === "paid")
    .reduce((sum, i) => sum + i.amount, 0);

  const overdueCount = invoices.filter(i => i.status === "overdue").length;

  const isQbo = (sub) => !!sub.qbId;

  return (
    <div className="space-y-6">

      {/* ACCOUNT CREDIT BANNER */}
      {credit > 0 && (
        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl p-4 shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-white bg-opacity-20 rounded-full p-2">
                <DollarSign className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-blue-100">Account Credit</p>
                <p className="text-2xl font-bold">${credit.toFixed(2)}</p>
              </div>
            </div>
            <p className="text-sm text-blue-100">
              This credit will be applied to your next bill
            </p>
          </div>
        </div>
      )}

      {/* STATS */}
      <div className="grid md:grid-cols-4 gap-6">
        <StatCard
          title="Active Services"
          value={activeSubs.length}
          icon={<Package />}
          color="green"
        />
        <StatCard
          title="Overdue Bills"
          value={overdueCount}
          icon={<AlertCircle />}
          color="red"
        />
        <StatCard
          title="Pending Bills"
          value={`${totalPending.toFixed(2)}`}
          icon={<FileText />}
          color="yellow"
        />
        <StatCard
          title="Total Paid"
          value={`${totalPaid.toFixed(2)}`}
          icon={<DollarSign />}
          color="green"
        />
      </div>

      {/* ACTIVE SUBSCRIPTIONS */}
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Active Services</h3>

        {activeSubs.length === 0 ? (
          <p className="text-gray-500 text-sm">No active subscriptions found.</p>
        ) : (
          <div className="space-y-3">
            {activeSubs.map(sub => {
              const service = services.find(s => s.id === sub.serviceId);

              return (
                <div
                  key={sub.id}
                  className="flex items-center justify-between py-2 border-b last:border-0"
                >
                  <div>
                    <p className="font-medium flex items-center gap-2">
                      {service?.name || "QuickBooks Subscription"}

                      {isQbo(sub) && (
                        <span className="text-xs bg-[#1B2D5C] text-white px-2 py-0.5 rounded">
                          QBO
                        </span>
                      )}
                    </p>

                    <p className="text-sm text-gray-500">
                      Next billing: {sub.nextBilling}
                    </p>
                  </div>

                  <span className="font-semibold text-[#1B2D5C]">
                    ${(sub.customPrice ?? service?.price ?? 0).toFixed(2)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* RECENT INVOICES */}
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Recent Invoices</h3>
        
        {invoices.length === 0 ? (
          <p className="text-gray-500 text-sm">No invoices found.</p>
        ) : (
          <div className="space-y-3">
            {invoices.slice(0, 5).map(inv => (
              <div key={inv.id} className="flex items-center justify-between py-2 border-b last:border-0">
                <div>
                  <p className="font-medium">Invoice #{inv.id.replace('qb_', '')}</p>
                  <p className="text-sm text-gray-500">Due: {inv.dueDate}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold">${inv.amount.toFixed(2)}</p>
                  <span className={`text-xs px-2 py-1 rounded ${
                    inv.status === 'paid' ? 'bg-[#dce4f5] text-[#0f1e3d]' :
                    inv.status === 'overdue' ? 'bg-red-100 text-red-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {invoiceStatusLabel(inv.status)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
};


const CustomerServices = ({ subscriptions, services, onUpdate }) => {
  const isQbo = (sub) => !!sub.qbId; // subscription came from QuickBooks
  const [modifyingSub, setModifyingSub] = useState(null);
  const [cancelingSub, setCancelingSub] = useState(null);

  const handleCancelService = async (subId) => {
    if (!(await showConfirm('Are you sure you want to cancel this service? This will stop future billing.'))) {
      return;
    }

    try {
      const res = await fetch(`/api/subscriptions/${subId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${auth.getToken()}`
        }
      });

      if (!res.ok) throw new Error('Failed to cancel service');

      safeShowAlert('Service cancelled successfully!');
      onUpdate();
      setCancelingSub(null);
    } catch (err) {
      safeShowAlert('Error cancelling service: ' + err.message);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">My Services</h2>

      <div className="grid md:grid-cols-2 gap-6">
        {subscriptions.map(sub => {
          const service = services.find(s => s.id === sub.serviceId);

          return (
            <div key={sub.id} className="bg-white rounded-xl shadow p-6">

              {/* HEADER */}
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {service?.name || "QuickBooks Subscription"}
                  </h3>

                  {isQbo(sub) && (
                    <span className="text-xs bg-[#1B2D5C] text-white px-2 py-0.5 rounded mt-1 inline-block">
                      Synced from QuickBooks
                    </span>
                  )}
                </div>

                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  sub.status === 'active' ? 'bg-[#dce4f5] text-[#0f1e3d]' : 'bg-gray-100 text-gray-700'
                }`}>
                  {sub.status}
                </span>
              </div>

              {/* BODY */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Monthly Rate:</span>
                  <span className="font-semibold">
                    ${(sub.customPrice ?? service?.price ?? 0).toFixed(2)}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Started:</span>
                  <span>{sub.startDate}</span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Next Billing:</span>
                  <span>{sub.nextBilling}</span>
                </div>
              </div>

              {/* ACTIONS */}
              {!isQbo(sub) && (
                <div className="mt-4 pt-4 border-t flex space-x-2">
                  <button
                    onClick={() => setModifyingSub(sub)}
                    className="flex-1 text-sm text-gray-600 hover:text-gray-900 font-medium"
                  >
                    Modify Service
                  </button>
                  <button
                    onClick={() => handleCancelService(sub.id)}
                    className="flex-1 text-sm text-red-600 hover:text-red-700 font-medium"
                  >
                    Cancel Service
                  </button>
                </div>
              )}

              {isQbo(sub) && (
                <div className="mt-4 pt-4 border-t text-xs text-gray-500">
                  This subscription is managed in QuickBooks and cannot be edited here.
                </div>
              )}

            </div>
          );
        })}
      </div>

      {/* Modify Service Modal */}
      {modifyingSub && (
        <ModifyServiceModal
          subscription={modifyingSub}
          service={services.find(s => s.id === modifyingSub.serviceId)}
          onClose={() => setModifyingSub(null)}
          onSuccess={() => {
            setModifyingSub(null);
            onUpdate();
          }}
        />
      )}
    </div>
  );
};

// Modify Service Modal (for customers to request service changes)
const ModifyServiceModal = ({ subscription, service, onClose, onSuccess }) => {
  const [customerId, setCustomerId] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Get customer ID from token
    try {
      const token = auth.getToken();
      const payload = JSON.parse(atob(token.split('.')[1]));
      setCustomerId(payload.id);
    } catch (err) {
      console.error('Failed to get customer ID:', err);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Create a service request for modification
      const res = await fetch(`/api/service-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify({
          customerId,
          serviceId: subscription.serviceId,
          notes: `Modification request for ${service?.name}: ${note}`
        })
      });

      if (!res.ok) throw new Error('Failed to submit request');

      safeShowAlert('Service modification request submitted! We will contact you soon.');
      onSuccess();
    } catch (err) {
      safeShowAlert('Error: ' + err.message);
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">Modify Service</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current Service</label>
            <p className="text-lg font-semibold">{service?.name}</p>
            <p className="text-sm text-gray-600">
              ${(subscription.customPrice ?? service?.price ?? 0).toFixed(2)}/month
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What would you like to change?
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="E.g., I'd like to upgrade to a larger bin, change pickup day, etc."
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
              rows="4"
              required
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
            <p>We'll review your request and contact you to confirm the changes.</p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-[#1B2D5C] text-white rounded-lg hover:bg-[#0f1e3d] disabled:opacity-50"
            >
              {loading ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CustomerBilling = ({ invoices, customerId, credit = 0, onCreditUpdate }) => {
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showLoadAccount, setShowLoadAccount] = useState(false);
  const [loadAmount, setLoadAmount] = useState('');
  const [loadingFunds, setLoadingFunds] = useState(false);
  const [processingFees, setProcessingFees] = useState({ card: 0, ach: 0 });

  // Load processing fee settings
  useEffect(() => {
    const loadFees = async () => {
      try {
        const res = await fetch('/api/settings');
        const settings = await res.json();
        setProcessingFees({
          card: parseFloat(settings.card_processing_fee || '0'),
          ach: parseFloat(settings.ach_processing_fee || '0')
        });
      } catch (err) {
        console.error('Failed to load processing fees:', err);
      }
    };
    loadFees();
  }, []);

  const calculateProcessingFee = (amount) => {
    // Assume card payment for load account (most common)
    return amount * (processingFees.card / 100);
  };

  const handleLoadAccount = async () => {
    const amount = parseFloat(loadAmount);
    if (isNaN(amount) || amount < 5) {
      safeShowAlert('Minimum amount is $5.00');
      return;
    }

    setLoadingFunds(true);
    try {
      const res = await fetch('/api/stripe/load-account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify({ customerId, amount })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      const feeMsg = data.processingFee > 0 ? ` (Processing fee: $${data.processingFee.toFixed(2)}, Total charged: $${data.totalCharged.toFixed(2)})` : '';
      safeShowAlert(`Successfully added $${amount.toFixed(2)} to your account!${feeMsg} New balance: $${data.newCredit.toFixed(2)}`);
      setShowLoadAccount(false);
      setLoadAmount('');
      if (onCreditUpdate) onCreditUpdate();
    } catch (err) {
      safeShowAlert('Error: ' + err.message);
    }
    setLoadingFunds(false);
  };

  const presetAmounts = [25, 50, 100, 200];

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Billing & Payments</h2>

      {/* Account Credit Section */}
      <div className="bg-white rounded-xl shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Account Balance</h3>
          <button
            onClick={() => setShowLoadAccount(!showLoadAccount)}
            className="flex items-center space-x-2 px-4 py-2 bg-[#1B2D5C] text-white rounded-lg hover:bg-[#0f1e3d]"
          >
            <Plus className="w-4 h-4" />
            <span>Add Funds</span>
          </button>
        </div>

        {/* Current Balance */}
        <div className={`p-4 rounded-lg ${credit > 0 ? 'bg-[#eef1fa] border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
          <div className="flex items-center space-x-3">
            <div className={`rounded-full p-3 ${credit > 0 ? 'bg-[#dce4f5]' : 'bg-gray-200'}`}>
              <DollarSign className={`w-6 h-6 ${credit > 0 ? 'text-[#1B2D5C]' : 'text-gray-500'}`} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Available Credit</p>
              <p className={`text-2xl md:text-3xl font-bold ${credit > 0 ? 'text-[#0f1e3d]' : 'text-gray-700'}`}>
                ${credit.toFixed(2)}
              </p>
            </div>
          </div>
          {credit > 0 && (
            <p className="text-sm text-[#1B2D5C] mt-2">
              This balance will be applied automatically to your next bill
            </p>
          )}
        </div>

        {/* Load Account Form */}
        {showLoadAccount && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-3">Add Funds to Your Account</h4>
            <p className="text-sm text-blue-700 mb-4">
              Prepay for services and the balance will be applied to future bills automatically.
            </p>

            {/* Preset Amounts */}
            <div className="grid grid-cols-4 gap-2 mb-4">
              {presetAmounts.map(amt => (
                <button
                  key={amt}
                  onClick={() => setLoadAmount(amt.toString())}
                  className={`py-2 px-3 rounded-lg border text-center font-medium transition-colors ${
                    loadAmount === amt.toString()
                      ? 'bg-[#1B2D5C] text-white border-[#1B2D5C]'
                      : 'bg-white border-gray-300 text-gray-700 hover:border-green-500'
                  }`}
                >
                  ${amt}
                </button>
              ))}
            </div>

            {/* Custom Amount */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Or enter custom amount</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  min="5"
                  step="0.01"
                  value={loadAmount}
                  onChange={(e) => setLoadAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-8 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Minimum $5.00</p>
            </div>

            {/* Fee Breakdown */}
            {loadAmount && parseFloat(loadAmount) >= 5 && processingFees.card > 0 && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount to add:</span>
                    <span className="font-medium">${parseFloat(loadAmount).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Processing fee ({processingFees.card}%):</span>
                    <span className="font-medium">${calculateProcessingFee(parseFloat(loadAmount)).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-gray-300">
                    <span className="font-semibold text-gray-900">Total charge:</span>
                    <span className="font-semibold text-gray-900">${(parseFloat(loadAmount) + calculateProcessingFee(parseFloat(loadAmount))).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowLoadAccount(false);
                  setLoadAmount('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleLoadAccount}
                disabled={loadingFunds || !loadAmount || parseFloat(loadAmount) < 5}
                className="flex-1 px-4 py-2 bg-[#1B2D5C] text-white rounded-lg hover:bg-[#0f1e3d] disabled:opacity-50"
              >
                {loadingFunds ? 'Processing...' : `Add $${parseFloat(loadAmount || 0).toFixed(2)}`}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Payment Method Section */}
      <PaymentMethodDisplay customerId={customerId} />

      {/* Desktop table */}
      <div className="hidden md:block bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">AutoPay</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {invoices.map(invoice => (
              <tr key={invoice.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">{invoice.id}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{invoice.date}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{invoice.dueDate}</td>
                <td className="px-6 py-4 text-sm font-semibold text-gray-900">${invoice.amount.toFixed(2)}</td>
                <td className="px-6 py-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    invoice.status === 'paid' ? 'bg-[#dce4f5] text-[#0f1e3d]' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {invoiceStatusLabel(invoice.status)}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <button onClick={() => setSelectedInvoice(invoice)} className="text-[#1B2D5C] hover:text-[#0f1e3d] font-medium text-sm mr-3">
                    View
                  </button>
                  {invoice.status === 'pending' && (
                    <button
                      onClick={() => payInvoice(invoice, 'stripe')}
                      className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                    >
                      Pay Now
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile card layout */}
      <div className="md:hidden space-y-3">
        {invoices.map(invoice => (
          <div key={invoice.id} className="bg-white rounded-xl shadow p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-900">Invoice #{invoice.id}</span>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                invoice.status === 'paid' ? 'bg-[#dce4f5] text-[#0f1e3d]' : 'bg-yellow-100 text-yellow-700'
              }`}>
                {invoiceStatusLabel(invoice.status)}
              </span>
            </div>
            <div className="text-sm text-gray-500 space-y-1 mb-3">
              <div className="flex justify-between">
                <span>Date:</span>
                <span>{invoice.date}</span>
              </div>
              <div className="flex justify-between">
                <span>Due:</span>
                <span>{invoice.dueDate}</span>
              </div>
              <div className="flex justify-between">
                <span>Amount:</span>
                <span className="font-semibold text-gray-900">${invoice.amount.toFixed(2)}</span>
              </div>
            </div>
            <div className="flex items-center space-x-3 pt-2 border-t border-gray-100">
              <button onClick={() => setSelectedInvoice(invoice)} className="text-[#1B2D5C] hover:text-[#0f1e3d] font-medium text-sm">
                View
              </button>
              {invoice.status === 'pending' && (
                <button
                  onClick={() => payInvoice(invoice, 'stripe')}
                  className="ml-auto px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
                >
                  Pay Now
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {selectedInvoice && (
        <CustomerInvoiceModal invoice={selectedInvoice} onClose={() => setSelectedInvoice(null)} />
      )}
    </div>
  );
};

// Customer Account Settings
const CustomerAccountSettings = ({ customerId, onUpdate }) => {
  const [customer, setCustomer] = useState(null);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPaymentMethodSetup, setShowPaymentMethodSetup] = useState(false);

  useEffect(() => {
    loadCustomer();
  }, [customerId]);

  const loadCustomer = async () => {
    try {
      const res = await fetch(`/api/customers/${customerId}`, {
        headers: { 'Authorization': `Bearer ${auth.getToken()}` }
      });
      const data = await res.json();
      setCustomer(data);
      setFormData(data);
    } catch (err) {
      console.error('Failed to load customer:', err);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/customers/${customerId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify(formData)
      });

      if (!res.ok) throw new Error('Failed to update profile');

      safeShowAlert('Profile updated successfully!');
      setEditing(false);
      loadCustomer();
      onUpdate();
    } catch (err) {
      safeShowAlert('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!customer) return <div>Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Account Settings</h2>
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            className="px-4 py-2 bg-[#1B2D5C] text-white rounded-lg hover:bg-[#0f1e3d]"
          >
            Edit Profile
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            {editing ? (
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
              />
            ) : (
              <p className="text-gray-900">{customer.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            {editing ? (
              <input
                type="email"
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
              />
            ) : (
              <p className="text-gray-900">{customer.email}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            {editing ? (
              <input
                type="tel"
                value={formData.phone || ''}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
              />
            ) : (
              <p className="text-gray-900">{customer.phone || 'Not provided'}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
            {editing ? (
              <input
                type="text"
                value={formData.address || ''}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
              />
            ) : (
              <p className="text-gray-900">{customer.address || 'Not provided'}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              {editing ? (
                <input
                  type="text"
                  value={formData.city || ''}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
                />
              ) : (
                <p className="text-gray-900">{customer.city || 'Not provided'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
              {editing ? (
                <input
                  type="text"
                  value={formData.zip || ''}
                  onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
                />
              ) : (
                <p className="text-gray-900">{customer.zip || 'Not provided'}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Date</label>
            {editing ? (
              <select
                value={formData.pickupDay || ''}
                onChange={(e) => setFormData({ ...formData, pickupDay: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
              >
                <option value="">Select a day...</option>
                <option value="Monday">Monday</option>
                <option value="Tuesday">Tuesday</option>
                <option value="Wednesday">Wednesday</option>
                <option value="Thursday">Thursday</option>
                <option value="Friday">Friday</option>
                <option value="Saturday">Saturday</option>
                <option value="Sunday">Sunday</option>
              </select>
            ) : (
              <p className="text-gray-900 font-bold">{customer.pickupDay || 'Not set'}</p>
            )}
          </div>

          {editing && (
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                onClick={() => {
                  setEditing(false);
                  setFormData(customer);
                }}
                disabled={loading}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="px-4 py-2 bg-[#1B2D5C] text-white rounded-lg hover:bg-[#0f1e3d] disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Payment Method Section */}
      <div className="bg-white rounded-xl shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Payment Method</h3>
          <button
            onClick={() => setShowPaymentMethodSetup(true)}
            className="text-sm text-[#1B2D5C] hover:text-[#0f1e3d] font-medium"
          >
            {customer.stripePaymentMethodId ? 'Change Payment Method' : 'Add Payment Method'}
          </button>
        </div>
        <PaymentMethodDisplay customerId={customerId} />
      </div>


      {/* AutoPay Settings Section */}
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="text-lg font-semibold mb-4">AutoPay Settings</h3>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-gray-700 mb-2">
              Automatically pay your monthly bills on the 5th of each month
            </p>
            <p className="text-sm text-gray-500">
              {customer.autoPayEnabled
                ? 'Your bills will be charged automatically to your saved payment method.'
                : 'Enable AutoPay to avoid late fees and never miss a payment.'}
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer ml-4">
            <input
              type="checkbox"
              checked={customer.autoPayEnabled === 1}
              onChange={async (e) => {
                const enabled = e.target.checked;
                try {
                  const res = await fetch(`/api/customers/${customerId}/autopay`, {
                    method: 'PATCH',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${auth.getToken()}`
                    },
                    body: JSON.stringify({ autoPayEnabled: enabled })
                  });

                  if (!res.ok) throw new Error('Failed to update autopay');

                  showAlert(`AutoPay ${enabled ? 'enabled' : 'disabled'} successfully!`);
                  loadCustomer();
                  onUpdate();
                } catch (err) {
                  showAlert('Error updating AutoPay settings.');
                  console.error('[AUTOPAY TOGGLE ERROR]', err);
                }
              }}
              className="sr-only peer"
            />
            <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-[#1B2D5C]"></div>
          </label>
        </div>
        {customer.autoPayEnabled === 1 && !customer.stripePaymentMethodId && (
          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-800">
              <strong>Warning:</strong> Please add a payment method to ensure AutoPay works correctly.
            </p>
          </div>
        )}
      </div>

      {/* Payment Method Setup Modal */}
      {showPaymentMethodSetup && (
        <Elements stripe={stripePromise}>
          <PaymentMethodSetupModal
            customerId={customerId}
            onSuccess={() => {
              setShowPaymentMethodSetup(false);
              loadCustomer();
            }}
            onClose={() => setShowPaymentMethodSetup(false)}
          />
        </Elements>
      )}
    </div>
  );
};

// ===============================================
// REPLACE NewServiceRequest component in App.jsx
// ===============================================

const NewServiceRequest = ({ services, customerId, onUpdate }) => {
  const [selectedService, setSelectedService] = useState(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showAutoPayEnrollment, setShowAutoPayEnrollment] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [loadingCustomer, setLoadingCustomer] = useState(true);
  const [showPaymentMethodSetup, setShowPaymentMethodSetup] = useState(false);

  // Load customer data to check for payment method
  useEffect(() => {
    const loadCustomer = async () => {
      try {
        const res = await fetch(`/api/customers/${customerId}`, {
          headers: { Authorization: `Bearer ${auth.getToken()}` }
        });
        const data = await res.json();
        setCustomer(data);
      } catch (err) {
        console.error('Error loading customer:', err);
      } finally {
        setLoadingCustomer(false);
      }
    };
    loadCustomer();
  }, [customerId]);

  const hasPaymentMethod = customer?.stripePaymentMethodId;

  const handleSubmit = async () => {
    if (!selectedService) {
      safeShowAlert("Please select a service");
      return;
    }

    if (!hasPaymentMethod) {
      safeShowAlert("Please add a payment method before requesting service");
      return;
    }

    setSubmitting(true);

    try {
      // Check if customer has any existing subscriptions
      const subsRes = await fetch(`/api/subscriptions?customer=${customerId}`, {
        headers: { Authorization: `Bearer ${auth.getToken()}` }
      });
      const existingSubs = await subsRes.json();
      const isFirstService = existingSubs.length === 0;

      const res = await fetch(`/api/service-requests`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify({
          customerId,
          serviceId: selectedService.id,
          notes
        })
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.error || "Request failed");

      safeShowAlert("Service request submitted! Our team will review it shortly.");
      setSelectedService(null);
      setNotes('');
      onUpdate();

      // Show autopay enrollment modal if this is their first service
      if (isFirstService) {
        if (customer.stripePaymentMethodId && !customer.autoPayEnabled) {
          setShowAutoPayEnrollment(true);
        }
      }
    } catch (err) {
      safeShowAlert("Error: " + err.message);
    }

    setSubmitting(false);
  };
  const groupedServices = {
    residential: services.filter(s => s.category === 'residential'),
    commercial: services.filter(s => s.category === 'commercial'),
    dumpster: services.filter(s => s.category === 'dumpster'),
    additional: services.filter(s => s.category === 'additional'),
  };

  return (
    <div className="space-y-6">
      {/* Payment Method Required Notice */}
      {!loadingCustomer && !hasPaymentMethod && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-red-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm text-red-800 font-semibold mb-2">
                Payment Method Required
              </p>
              <p className="text-sm text-red-700 mb-3">
                You must add a payment method before requesting service. This ensures we can bill you for approved services.
              </p>
              <button
                onClick={() => setShowPaymentMethodSetup(true)}
                className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-red-700 transition"
              >
                Add Payment Method
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800 mb-2">
          <strong>Note:</strong> New service requests require admin approval before they're activated.
        </p>
        <p className="text-sm text-blue-800">
          <strong>Billing:</strong> Upon approval, you will be billed for your first month of service using your saved payment method.
        </p>
      </div>

      <h2 className="text-2xl font-bold">Request New Service</h2>

      {Object.entries(groupedServices).map(([category, items]) => (
        items.length > 0 && (
          <div key={category} className="space-y-4">
            <h3 className="text-xl font-semibold capitalize text-gray-800">{category} Services</h3>
            <div className="grid md:grid-cols-3 gap-6">
              {items.map(service => (
                <div
                  key={service.id}
                  onClick={() => setSelectedService(service)}
                  className={`bg-white rounded-xl shadow p-6 cursor-pointer transition border-2 ${
                    selectedService?.id === service.id ? 'border-[#1B2D5C] ring-2 ring-green-200' : 'border-transparent hover:border-gray-200'
                  }`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="font-semibold text-gray-900">{service.name}</h4>
                    {selectedService?.id === service.id && (
                      <Check className="w-6 h-6 text-[#1B2D5C]" />
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-4">{service.description}</p>
                  {service.units !== null && (
                    <p className="text-xs text-blue-600 mb-2">Available: {service.units} units</p>
                  )}
                  <div className="pt-3 border-t">
                    <span className="text-2xl font-bold text-[#1B2D5C]">${service.price}</span>
                    <span className="text-sm text-gray-500 ml-1">
                      {category === 'dumpster' ? 'rental' : '/month'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      ))}

      {selectedService && (
        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Complete Your Request</h3>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-2">Selected Service:</p>
              <p className="font-semibold text-lg">{selectedService.name}</p>
              <p className="text-gray-600">{selectedService.description}</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any special requests or details..."
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
                rows="3"
              />
            </div>
            
            <div className="pt-4 border-t">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <span className="text-lg font-medium">Monthly Cost:</span>
                  <p className="text-xs text-gray-500">Subject to admin approval</p>
                </div>
                <span className="text-2xl font-bold text-[#1B2D5C]">${selectedService.price}</span>
              </div>
              
              <button
                onClick={handleSubmit}
                disabled={submitting || !hasPaymentMethod}
                className="w-full bg-[#1B2D5C] text-white py-3 rounded-lg font-semibold hover:bg-[#0f1e3d] transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting...' : !hasPaymentMethod ? 'Payment Method Required' : 'Submit Request'}
              </button>
              {!hasPaymentMethod && (
                <p className="text-sm text-red-600 text-center mt-2">
                  Please add a payment method to continue
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* AutoPay Enrollment Modal */}
      {showAutoPayEnrollment && (
        <AutoPayEnrollmentModal
          customerId={customerId}
          onClose={() => setShowAutoPayEnrollment(false)}
          onEnroll={(enabled) => {
            setShowAutoPayEnrollment(false);
            if (enabled) {
              showAlert('AutoPay enabled! Your bills will be automatically paid on the 5th of each month.');
            }
          }}
        />
      )}

      {/* Payment Method Setup Modal */}
      {showPaymentMethodSetup && (
        <PaymentMethodSetupModal
          customerId={customerId}
          onClose={() => setShowPaymentMethodSetup(false)}
          onSuccess={async () => {
            // Reload customer data after adding payment method
            try {
              const res = await fetch(`/api/customers/${customerId}`, {
                headers: { Authorization: `Bearer ${auth.getToken()}` }
              });
              const data = await res.json();
              setCustomer(data);
              setShowPaymentMethodSetup(false);
              safeShowAlert('Payment method added successfully! You can now request services.');
            } catch (err) {
              console.error('Error reloading customer:', err);
            }
          }}
        />
      )}
    </div>
  );
};



// Modals
// =========================
// Add Customer Modal (Updated with Password + Role)
// =========================
// =========================
// Add Customer Modal (FULLY PATCHED)
// Uses /api/auth/signup so admin-created customers match real DB customers
// =========================

const AddCustomerModal = ({ onClose, onUpdate }) => {
  const API_BASE = process.env.REACT_APP_API_BASE || "";

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    address: "",
    city: "",
    zip: "",
    type: "residential",
  });

  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [addrSuggestion, setAddrSuggestion] = useState(null);

  const doCreate = async (addr, cty, zp) => {
    setBusy(true);
    try {
      const res = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          phone: formData.phone,
          address: addr,
          city: cty,
          zip: zp,
          type: formData.type,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Signup failed');
      await onUpdate();
      onClose();
    } catch (err) {
      setError(err.message);
    }
    setBusy(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (formData.address && formData.city) {
      setBusy(true);
      try {
        const valRes = await fetch('/api/address/validate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ address: formData.address, city: formData.city, zip: formData.zip }),
        });
        const valData = await valRes.json();
        if (valData.match) {
          const m = valData.match;
          const isDifferent =
            m.address.toLowerCase() !== formData.address.trim().toLowerCase() ||
            m.city.toLowerCase() !== formData.city.trim().toLowerCase();
          if (isDifferent) {
            setAddrSuggestion(m);
            setBusy(false);
            return;
          }
        }
      } catch (_) {}
      setBusy(false);
    }

    await doCreate(formData.address, formData.city, formData.zip);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      {addrSuggestion && (
        <AddressConfirmDialog
          current={{ address: formData.address, city: formData.city, zip: formData.zip }}
          suggested={addrSuggestion}
          onUse={() => {
            const m = addrSuggestion;
            setFormData(fd => ({ ...fd, address: m.address, city: m.city, zip: m.zip || fd.zip }));
            setAddrSuggestion(null);
            doCreate(m.address, m.city, m.zip || formData.zip);
          }}
          onKeep={() => { setAddrSuggestion(null); doCreate(formData.address, formData.city, formData.zip); }}
        />
      )}
      <div className="bg-white rounded-xl max-w-2xl w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
        
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Add New Customer</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">

          {/* Name + Email */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              type="password"
              required
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              type="tel"
              required
              value={formData.phone}
              onChange={(e) =>
                setFormData({ ...formData, phone: e.target.value })
              }
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <input
              type="text"
              required
              value={formData.address}
              onChange={(e) =>
                setFormData({ ...formData, address: e.target.value })
              }
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>

          {/* City + ZIP */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input
                type="text"
                required
                value={formData.city}
                onChange={(e) =>
                  setFormData({ ...formData, city: e.target.value })
                }
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ZIP
              </label>
              <input
                type="text"
                required
                value={formData.zip}
                onChange={(e) =>
                  setFormData({ ...formData, zip: e.target.value })
                }
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Service Type
            </label>
            <select
              value={formData.type}
              onChange={(e) =>
                setFormData({ ...formData, type: e.target.value })
              }
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="residential">Residential</option>
              <option value="commercial">Commercial</option>
            </select>
          </div>

          {/* Buttons */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={busy}
              className="flex-1 px-4 py-2 bg-[#1B2D5C] text-white rounded-lg hover:bg-[#0f1e3d]"
            >
              {busy ? "Creating..." : "Create Customer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


const CustomerDetailModal = ({ customer, subscriptions, services, invoices, onClose, onUpdate }) => {
  const [showAddService, setShowAddService] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [locations, setLocations] = useState([]);
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [credit, setCredit] = useState(customer.credit || 0);
  const [editingCredit, setEditingCredit] = useState(false);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [creditLoading, setCreditLoading] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showRecordPaymentModal, setShowRecordPaymentModal] = useState(null);
  const [showCreateInvoiceModal, setShowCreateInvoiceModal] = useState(false);
  const [formData, setFormData] = useState({
    name: customer.name,
    email: customer.email,
    phone: customer.phone || '',
    address: customer.address || '',
    city: customer.city || '',
    zip: customer.zip || '',
    type: customer.type || 'residential',
    status: customer.status || 'active',
    notes: customer.notes || '',
    pickupDay: customer.pickupDay || ''
  });

  useEffect(() => {
    loadLocations();
    loadCredit();
  }, []);

  const loadLocations = async () => {
    try {
      const res = await fetch(`/api/locations/customer/${customer.id}`);
      const data = await res.json();
      setLocations(data);
    } catch (err) {
      console.error('Failed to load locations:', err);
    }
  };

  const loadCredit = async () => {
    try {
      const res = await fetch(`/api/customers/${customer.id}/credit`);
      const data = await res.json();
      setCredit(data.credit || 0);
    } catch (err) {
      console.error('Failed to load credit:', err);
    }
  };

  const handleAddCredit = async () => {
    const amount = parseFloat(creditAmount);
    if (isNaN(amount) || amount <= 0) {
      safeShowAlert('Please enter a valid positive amount');
      return;
    }
    setCreditLoading(true);
    try {
      const res = await fetch(`/api/customers/${customer.id}/credit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount, reason: creditReason || 'Added by admin' })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setCredit(data.newCredit);
      setCreditAmount('');
      setCreditReason('');
      setEditingCredit(false);
      safeShowAlert(`Added $${amount.toFixed(2)} credit. New balance: $${data.newCredit.toFixed(2)}`);
      onUpdate();
    } catch (err) {
      safeShowAlert('Error: ' + err.message);
    }
    setCreditLoading(false);
  };

  const handleSetCredit = async (newAmount) => {
    const amount = parseFloat(newAmount);
    if (isNaN(amount) || amount < 0) {
      safeShowAlert('Please enter a valid amount (0 or more)');
      return;
    }
    if (!(await showConfirm(`Set credit balance to $${amount.toFixed(2)}?`))) return;
    setCreditLoading(true);
    try {
      const res = await fetch(`/api/customers/${customer.id}/credit`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount, reason: 'Set by admin' })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setCredit(data.newCredit);
      safeShowAlert(`Credit set to $${data.newCredit.toFixed(2)}`);
      onUpdate();
    } catch (err) {
      safeShowAlert('Error: ' + err.message);
    }
    setCreditLoading(false);
  };

  const deleteInvoice = async (invoice) => {
    if (!(await showConfirm(`Delete invoice ${invoice.id}? This cannot be undone.`)))
      return;
    try {
      const res = await fetch(`/api/billing/invoices/${invoice.id}`, {
        method: 'DELETE'
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to delete');
      }
      onUpdate();
    } catch (err) {
      safeShowAlert('Error: ' + err.message);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`/api/customers/${customer.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (!res.ok) throw new Error('Failed to update customer');
      setEditing(false);
      onUpdate();
    } catch (err) {
      safeShowAlert('Error: ' + err.message);
    }
    setSaving(false);
  };

  const handleCancel = () => {
    setFormData({
      name: customer.name,
      email: customer.email,
      phone: customer.phone || '',
      address: customer.address || '',
      city: customer.city || '',
      zip: customer.zip || '',
      type: customer.type || 'residential',
      status: customer.status || 'active',
      notes: customer.notes || '',
      pickupDay: customer.pickupDay || ''
    });
    setEditing(false);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      safeShowAlert('Password must be at least 6 characters');
      return;
    }
    setPasswordLoading(true);
    try {
      const res = await fetch(`/api/auth/admin-reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: customer.id, newPassword, sendEmail: true })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      safeShowAlert(`Password reset successfully for ${customer.name}`);
      setNewPassword('');
      setShowPasswordReset(false);
    } catch (err) {
      safeShowAlert('Error: ' + err.message);
    }
    setPasswordLoading(false);
  };

  const handleSendResetLink = async () => {
    if (!(await showConfirm(`Send password reset email to ${customer.email}?`)))
       return;
    setPasswordLoading(true);
    try {
      const res = await fetch(`/api/auth/admin-send-reset-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: customer.id })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      safeShowAlert(data.message);
    } catch (err) {
      safeShowAlert('Error: ' + err.message);
    }
    setPasswordLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">{editing ? 'Edit Customer' : formData.name}</h2>
          <div className="flex items-center space-x-3">
            {!editing ? (
              <button
                onClick={() => setEditing(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Edit className="w-4 h-4" />
                <span>Edit</span>
              </button>
            ) : (
              <>
                <button
                  onClick={handleCancel}
                  disabled={saving}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 bg-[#1B2D5C] text-white rounded-lg hover:bg-[#0f1e3d]"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-700">Contact Information</h3>
            {editing ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
                  >
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Date</label>
                  <select
                    value={formData.pickupDay || ''}
                    onChange={(e) => setFormData({...formData, pickupDay: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
                  >
                    <option value="">Select a day...</option>
                    <option value="Monday">Monday</option>
                    <option value="Tuesday">Tuesday</option>
                    <option value="Wednesday">Wednesday</option>
                    <option value="Thursday">Thursday</option>
                    <option value="Friday">Friday</option>
                    <option value="Saturday">Saturday</option>
                    <option value="Sunday">Sunday</option>
                  </select>
                </div>
              </div>
            ) : (
              <div className="space-y-2 text-sm">
                <p><span className="text-gray-600">Email:</span> {formData.email}</p>
                <p><span className="text-gray-600">Phone:</span> {formData.phone || 'N/A'}</p>
                <p><span className="text-gray-600">Type:</span> <span className="capitalize">{formData.type}</span></p>
                <p><span className="text-gray-600">Pickup Date:</span> <span className="font-bold">{formData.pickupDay || 'Not set'}</span></p>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <h3 className="font-semibold text-gray-700">Primary Address</h3>
            {editing ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
                    <input
                      type="text"
                      value={formData.zip}
                      onChange={(e) => setFormData({...formData, zip: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            ) : (
              <div className="space-y-2 text-sm">
                <p><span className="text-gray-600">Address:</span> {formData.address || 'N/A'}</p>
                <p><span className="text-gray-600">City:</span> {formData.city || 'N/A'}</p>
                <p><span className="text-gray-600">ZIP:</span> {formData.zip || 'N/A'}</p>
                <p><span className="text-gray-600">Status:</span> <span className="capitalize">{formData.status}</span></p>
                <p><span className="text-gray-600">Customer Since:</span> {customer.created?.split('T')[0] || 'N/A'}</p>
                <p><span className="text-gray-600">Active Services:</span> {subscriptions.length}</p>
              </div>
            )}
          </div>
        </div>

        {/* Notes Section */}
        <div className="border-t pt-6 mb-6">
          <h3 className="font-semibold text-gray-700 mb-3">Notes</h3>
          {editing ? (
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Add notes about this customer..."
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
              rows="4"
            />
          ) : (
            <p className="text-sm text-gray-700 whitespace-pre-wrap">
              {formData.notes || <span className="text-gray-400 italic">No notes</span>}
            </p>
          )}
        </div>

        {/* Account Credit Section */}
        <div className="border-t pt-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-gray-700">Account Credit</h3>
            {!editingCredit && (
              <button
                onClick={() => setEditingCredit(true)}
                className="text-[#1B2D5C] hover:text-[#0f1e3d] text-sm font-medium flex items-center space-x-1"
              >
                <Plus className="w-4 h-4" />
                <span>Add Credit</span>
              </button>
            )}
          </div>

          {/* Current Balance */}
          <div className={`p-4 rounded-lg mb-4 ${credit > 0 ? 'bg-[#eef1fa] border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`rounded-full p-2 ${credit > 0 ? 'bg-[#dce4f5]' : 'bg-gray-200'}`}>
                  <DollarSign className={`w-5 h-5 ${credit > 0 ? 'text-[#1B2D5C]' : 'text-gray-500'}`} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Current Credit Balance</p>
                  <p className={`text-2xl font-bold ${credit > 0 ? 'text-[#0f1e3d]' : 'text-gray-700'}`}>
                    ${credit.toFixed(2)}
                  </p>
                </div>
              </div>
              {credit > 0 && (
                <button
                  onClick={() => handleSetCredit(0)}
                  disabled={creditLoading}
                  className="text-red-600 hover:text-red-700 text-sm font-medium"
                >
                  Clear Credit
                </button>
              )}
            </div>
          </div>

          {/* Add Credit Form */}
          {editingCredit && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-3">Add Credit to Account</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={creditAmount}
                    onChange={(e) => setCreditAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
                  <input
                    type="text"
                    value={creditReason}
                    onChange={(e) => setCreditReason(e.target.value)}
                    placeholder="e.g., Refund for missed pickup, Loyalty bonus"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setEditingCredit(false);
                      setCreditAmount('');
                      setCreditReason('');
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddCredit}
                    disabled={creditLoading || !creditAmount}
                    className="px-4 py-2 bg-[#1B2D5C] text-white rounded-lg hover:bg-[#0f1e3d] disabled:opacity-50"
                  >
                    {creditLoading ? 'Adding...' : 'Add Credit'}
                  </button>
                </div>
              </div>
            </div>
          )}

          <p className="text-xs text-gray-500 mt-2">
            Credit is automatically applied to invoices before charging the customer's payment method.
          </p>
        </div>

        {/* Service Locations Section */}
        <div className="border-t pt-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-gray-700">Service Locations</h3>
            <button
              onClick={() => setShowAddLocation(true)}
              className="text-[#1B2D5C] hover:text-[#0f1e3d] text-sm font-medium flex items-center space-x-1"
            >
              <Plus className="w-4 h-4" />
              <span>Add Location</span>
            </button>
          </div>

          {locations.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No additional locations. Click "Add Location" to add service addresses.</p>
          ) : (
            <div className="space-y-3">
              {locations.map(loc => (
                <div key={loc.id} className="flex items-start justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      {loc.label && <span className="font-medium text-gray-900">{loc.label}</span>}
                      {loc.isPrimary === 1 && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">Primary</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-700">
                      {loc.address}
                      {loc.city && `, ${loc.city}`}
                      {loc.zip && ` ${loc.zip}`}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {subscriptions.filter(s => s.locationId === loc.id).length} service(s) at this location
                    </p>
                  </div>
                  <button
                    onClick={async () => {
                      if (await showConfirm('Delete this location?')) {
                        try {
                          const res = await fetch(`/api/locations/${loc.id}`, { method: 'DELETE' });
                          const data = await res.json();
                          if (!res.ok) throw new Error(data.error);
                          loadLocations();
                        } catch (err) {
                          safeShowAlert('Error: ' + err.message);
                        }
                      }
                    }}
                    className="text-red-600 hover:text-red-700 ml-4"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {showAddLocation && (
            <AddLocationModal
              customerId={customer.id}
              onClose={() => setShowAddLocation(false)}
              onSuccess={() => {
                loadLocations();
                setShowAddLocation(false);
              }}
            />
          )}
        </div>

        {/* Password Management Section */}
        <div className="border-t pt-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-gray-700">Password Management</h3>
            <button
              onClick={() => setShowPasswordReset(!showPasswordReset)}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              {showPasswordReset ? 'Cancel' : 'Reset Password'}
            </button>
          </div>

          {showPasswordReset && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Set New Password</label>
                <div className="flex gap-3">
                  <input
                    type="text"
                    placeholder="Enter new password (min 6 chars)"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
                  />
                  <button
                    onClick={handleResetPassword}
                    disabled={passwordLoading}
                    className="px-4 py-2 bg-[#1B2D5C] text-white rounded-lg hover:bg-[#0f1e3d] disabled:opacity-50"
                  >
                    {passwordLoading ? 'Saving...' : 'Set Password'}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Customer will receive an email notification</p>
              </div>

              <div className="border-t pt-4">
                <p className="text-sm text-gray-600 mb-2">Or send a reset link to the customer's email:</p>
                <button
                  onClick={handleSendResetLink}
                  disabled={passwordLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
                >
                  <Mail className="w-4 h-4" />
                  <span>{passwordLoading ? 'Sending...' : 'Send Reset Link'}</span>
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="border-t pt-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-gray-700">Active Subscriptions</h3>
            <button onClick={() => setShowAddService(true)} className="text-[#1B2D5C] hover:text-[#0f1e3d] text-sm font-medium">
              + Add Service
            </button>
          </div>
          <div className="space-y-3">
            {subscriptions.length === 0 ? (
              <p className="text-sm text-gray-500 italic text-center py-8">No active subscriptions</p>
            ) : (
              subscriptions.map(sub => {
                const service = services.find(s => s.id === sub.serviceId);
                const location = locations.find(l => l.id === sub.locationId);
                return (
                  <div key={sub.id} className="flex items-start justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <p className="font-medium">{service?.name}</p>
                        {sub.customPrice !== null && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">Custom Price</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">Next billing: {sub.nextBilling}</p>
                      {location ? (
                        <p className="text-xs text-gray-500 mt-1">
                          <MapPin className="w-3 h-3 inline mr-1" />
                          {location.label || location.address}
                        </p>
                      ) : (
                        <p className="text-xs text-gray-500 mt-1">
                          <Home className="w-3 h-3 inline mr-1" />
                          Primary Address
                        </p>
                      )}
                    </div>
                    <div className="text-right ml-4">
                      <p className="font-semibold text-lg mb-1">${(sub.customPrice !== null ? sub.customPrice : service?.price || 0).toFixed(2)}/mo</p>
                      <button
                        onClick={async () => {
                          if (await showConfirm('Cancel this subscription?')) {
                            await storage.delete('subscriptions', sub.id);
                            onUpdate();
                          }
                        }}
                        className="text-xs text-red-600 hover:text-red-700"
                      >
                        Cancel Service
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Invoice History Section */}
        <div className="border-t pt-6 mt-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-gray-700">Invoice History</h3>
            <button
              onClick={() => setShowCreateInvoiceModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-blue-700 text-sm"
            >
              <Plus className="w-4 h-4" />
              <span>Create Invoice</span>
            </button>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {!invoices || invoices.length === 0 ? (
              <p className="text-sm text-gray-500 italic text-center py-8">No invoices found</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {invoices.map(invoice => {
                      const isOverdue = invoice.status === 'pending' && invoice.dueDate < new Date().toISOString().split('T')[0];
                      return (
                        <tr key={invoice.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{invoice.id}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{invoice.date}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{invoice.dueDate}</td>
                          <td className="px-4 py-3 text-sm font-semibold text-gray-900">${(invoice.amount || 0).toFixed(2)}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              invoice.status === 'paid' ? 'bg-[#dce4f5] text-[#0f1e3d]' :
                              isOverdue ? 'bg-red-100 text-red-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {isOverdue ? 'Overdue' : invoiceStatusLabel(invoice.status)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => setSelectedInvoice(invoice)}
                                className="p-2 text-gray-600 hover:text-[#1B2D5C]"
                                title="View Details"
                              >
                                <FileText className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => window.open(`/api/billing/invoices/${invoice.id}/pdf`, '_blank')}
                                className="p-2 text-gray-600 hover:text-blue-600"
                                title="Download PDF"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                              <button
                                onClick={async () => {
                                  try {
                                    const res = await fetch(`/api/billing/invoices/${invoice.id}/send`, { method: 'POST' });
                                    const data = await res.json();
                                    safeShowAlert(data.message || 'Invoice sent!');
                                  } catch (err) {
                                    safeShowAlert('Failed to send invoice: ' + err.message);
                                  }
                                }}
                                className="p-2 text-gray-600 hover:text-purple-600"
                                title="Send Email"
                              >
                                <Mail className="w-4 h-4" />
                              </button>
                              {invoice.status !== 'paid' && (
                                <button
                                  onClick={() => setShowRecordPaymentModal(invoice)}
                                  className="p-2 text-gray-600 hover:text-[#1B2D5C]"
                                  title="Record Payment"
                                >
                                  <DollarSign className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={() => deleteInvoice(invoice)}
                                className="p-2 text-gray-600 hover:text-red-600"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {showAddService && (
          <AddServiceToCustomerModal
            customer={customer}
            services={services}
            locations={locations}
            onClose={() => setShowAddService(false)}
            onSuccess={() => {
              onUpdate();
              setShowAddService(false);
            }}
          />
        )}

        {/* Invoice Detail Modal */}
        {selectedInvoice && (
          <InvoiceDetailModal
            invoice={selectedInvoice}
            customer={customer}
            onClose={() => setSelectedInvoice(null)}
            onUpdate={onUpdate}
            onMarkPaid={async (invoice, method = 'manual', notes = '') => {
              try {
                const res = await fetch(`/api/billing/invoices/${invoice.id}/paid`, {
                  method: 'PATCH',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ method, notes })
                });
                if (!res.ok) throw new Error('Failed to mark as paid');
                onUpdate();
                setSelectedInvoice(null);
              } catch (err) {
                safeShowAlert('Error: ' + err.message);
              }
            }}
            onSendEmail={async (invoice) => {
              try {
                const res = await fetch(`/api/billing/invoices/${invoice.id}/send`, { method: 'POST' });
                const data = await res.json();
                safeShowAlert(data.message || 'Invoice sent!');
              } catch (err) {
                safeShowAlert('Failed to send invoice: ' + err.message);
              }
            }}
            onDownloadPDF={(invoice) => {
              window.open(`/api/billing/invoices/${invoice.id}/pdf`, '_blank');
            }}
          />
        )}

        {/* Record Payment Modal */}
        {showRecordPaymentModal && (
          <RecordPaymentModal
            invoice={showRecordPaymentModal}
            customer={customer}
            onClose={() => setShowRecordPaymentModal(null)}
            onSuccess={() => {
              onUpdate();
              setShowRecordPaymentModal(null);
            }}
          />
        )}

        {/* Create Invoice Modal */}
        {showCreateInvoiceModal && (
          <CreateInvoiceModal
            customers={[customer]}
            services={services}
            preselectedCustomerId={customer.id}
            onClose={() => setShowCreateInvoiceModal(false)}
            onCreated={() => {
              onUpdate();
              setShowCreateInvoiceModal(false);
            }}
          />
        )}
      </div>
    </div>
  );
};

const AddServiceModal = ({ onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    units: '',
    category: 'residential'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Generate unique ID for the service
    const serviceId = `svc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    await fetch(`/api/services`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: serviceId,
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        units: formData.units ? parseInt(formData.units) : null,
        category: formData.category
      })
    });

    onUpdate();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Add New Service</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Service Name</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              required
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
              rows="3"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price ($)</label>
              <input
                type="number"
                step="0.01"
                required
                value={formData.price}
                onChange={(e) => setFormData({...formData, price: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Available Units</label>
              <input
                type="number"
                placeholder="Optional"
                value={formData.units}
                onChange={(e) => setFormData({...formData, units: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({...formData, category: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
            >
              <option value="residential">Residential</option>
              <option value="commercial">Commercial</option>
              <option value="dumpster">Dumpster</option>
              <option value="additional">Additional Services</option>
            </select>
          </div>
          <div className="flex space-x-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" className="flex-1 px-4 py-2 bg-[#1B2D5C] text-white rounded-lg hover:bg-[#0f1e3d]">
              Add Service
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditServiceModal = ({ service, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    name: service.name,
    description: service.description,
    price: service.price,
    units: service.units,
    category: service.category
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    await fetch(`/api/services/${service.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        units: formData.units ? parseInt(formData.units) : null,
        category: formData.category
      })
    });

    onUpdate();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Edit Service</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Service Name</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              required
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
              rows="3"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price ($)</label>
              <input
                type="number"
                step="0.01"
                required
                value={formData.price}
                onChange={(e) => setFormData({...formData, price: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Available Units</label>
              <input
                type="number"
                placeholder="Optional"
                value={formData.units}
                onChange={(e) => setFormData({...formData, units: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({...formData, category: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
            >
              <option value="residential">Residential</option>
              <option value="commercial">Commercial</option>
              <option value="dumpster">Dumpster</option>
              <option value="additional">Additional Services</option>
            </select>
          </div>

          <div className="flex space-x-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" className="flex-1 px-4 py-2 bg-[#1B2D5C] text-white rounded-lg hover:bg-[#0f1e3d]">
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// =========================
// Invoice Detail Modal (Admin)
// =========================
const InvoiceDetailModal = ({ invoice, customer, onClose, onUpdate, onMarkPaid, onSendEmail, onDownloadPDF }) => {
  const [paymentMethod, setPaymentMethod] = useState('manual');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentDetails, setPaymentDetails] = useState(null);

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      if (invoice.status === 'paid' && invoice.id) {
        try {
          const response = await fetch(`/api/stripe/payments/by-invoice/${invoice.id}`);
          if (response.ok) {
            const payment = await response.json();
            setPaymentDetails(payment);
          }
        } catch (err) {
          console.error('Error fetching payment details:', err);
        }
      }
    };
    fetchPaymentDetails();
  }, [invoice.id, invoice.status]);

  const handleMarkPaid = async () => {
    if (onMarkPaid) {
      await onMarkPaid(invoice, paymentMethod, paymentNotes);
      onClose();
    }
  };

  const items = Array.isArray(invoice.items) ? invoice.items : [];
  const isOverdue = invoice.status === 'pending' && invoice.dueDate < new Date().toISOString().split('T')[0];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full p-4 sm:p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-bold">Invoice Details</h2>
            <span className={`inline-block mt-1 px-3 py-1 rounded-full text-xs font-medium ${
              invoice.status === 'paid' ? 'bg-[#dce4f5] text-[#0f1e3d]' :
              isOverdue ? 'bg-red-100 text-red-700' :
              'bg-yellow-100 text-yellow-700'
            }`}>
              {isOverdue ? 'OVERDUE' : invoiceStatusLabel(invoice.status).toUpperCase()}
            </span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
            <div>
              <p className="text-sm text-gray-600">Invoice ID</p>
              <p className="font-semibold">{invoice.id}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Customer</p>
              <p className="font-semibold">{customer?.name || 'Unknown'}</p>
              {customer?.email && <p className="text-xs text-gray-500">{customer.email}</p>}
            </div>
            <div>
              <p className="text-sm text-gray-600">Invoice Date</p>
              <p className="font-semibold">{invoice.date}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Due Date</p>
              <p className={`font-semibold ${isOverdue ? 'text-red-600' : ''}`}>{invoice.dueDate}</p>
            </div>
            {invoice.paid_at && (
              <div>
                <p className="text-sm text-gray-600">Paid On</p>
                <p className="font-semibold text-[#1B2D5C]">{new Date(invoice.paid_at).toLocaleDateString()}</p>
              </div>
            )}
            {invoice.payment_method && (
              <div>
                <p className="text-sm text-gray-600">Payment Method</p>
                <p className="font-semibold capitalize">{invoice.payment_method}</p>
                {paymentDetails?.referenceNumber && invoice.payment_method === 'check' && (
                  <p className="text-sm text-gray-600 mt-1">Check #: <span className="font-medium text-gray-900">{paymentDetails.referenceNumber}</span></p>
                )}
              </div>
            )}
            {invoice.payment_notes && (
              <div className="md:col-span-2">
                <p className="text-sm text-gray-600">Payment Notes</p>
                <p className="text-sm text-gray-900 whitespace-pre-wrap bg-gray-50 p-2 rounded border border-gray-200 mt-1">{invoice.payment_notes}</p>
              </div>
            )}
          </div>

          <div className="border-t pt-4">
            <h3 className="font-semibold mb-3">Line Items</h3>
            <div className="space-y-2">
              {items.length > 0 ? items.map((item, idx) => (
                <div key={idx} className="flex justify-between py-2 border-b last:border-0">
                  <span>{item.service || item.description || 'Service'}</span>
                  <span className="font-medium">${(parseFloat(item.amount) || 0).toFixed(2)}</span>
                </div>
              )) : (
                <p className="text-gray-500 text-sm">No line items</p>
              )}
            </div>
          </div>

          <div className="border-t pt-4 flex justify-between text-lg font-bold">
            <span>Total:</span>
            <span>${(invoice.amount || 0).toFixed(2)}</span>
          </div>

          {invoice.status !== 'paid' && (
            <div className="border-t pt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
                >
                  <option value="manual">Manual / Cash</option>
                  <option value="check">Check</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="credit_card">Credit Card</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Notes (Optional)</label>
                <textarea
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  placeholder="Add details about this payment (check number, reference, etc.)..."
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
                  rows="3"
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-3 pt-6 border-t mt-6">
          {onDownloadPDF && (
            <button onClick={() => onDownloadPDF(invoice)} className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center space-x-2">
              <Download className="w-4 h-4" />
              <span>Download PDF</span>
            </button>
          )}
          {onSendEmail && (
            <button onClick={() => onSendEmail(invoice)} className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center space-x-2">
              <Mail className="w-4 h-4" />
              <span>Send Email</span>
            </button>
          )}
          <div className="flex-1"></div>
          <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
            Close
          </button>
          {invoice.status !== 'paid' && (
            <button onClick={handleMarkPaid} className="px-4 py-2 bg-[#1B2D5C] text-white rounded-lg hover:bg-[#0f1e3d] flex items-center space-x-2">
              <Check className="w-4 h-4" />
              <span>Mark as Paid</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// =========================
// Customer Invoice Modal
// =========================
const CustomerInvoiceModal = ({ invoice, onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-xl max-w-2xl w-full p-4 sm:p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Invoice #{invoice.id}</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="w-6 h-6" />
        </button>
      </div>

      <div className="space-y-3">
        <p><span className="font-semibold">Date:</span> {invoice.date}</p>
        <p><span className="font-semibold">Due:</span> {invoice.dueDate}</p>

        <div className="border-t pt-4">
          <h3 className="font-semibold mb-2">Items</h3>
          <ul className="space-y-2">
            {invoice.items.map((item, idx) => (
              <li key={idx} className="flex justify-between">
                <span>{item.service}</span>
                <span>${item.amount.toFixed(2)}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="border-t pt-4 flex justify-between font-bold text-lg">
          <span>Total:</span>
          <span>${invoice.amount.toFixed(2)}</span>
        </div>
      </div>

      <div className="pt-6 flex justify-end space-x-3">
        <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Close</button>
        {invoice.status === 'pending' && (
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Pay Now
          </button>
        )}
      </div>
    </div>
  </div>
);

// =========================
// Add Promo Modal
// =========================
const AddPromoModal = ({ onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    code: '', description: '', discount: '', type: 'percent', expires: ''
  });

  const handleSubmit = async (e) => {
  e.preventDefault();

  await fetch(`/api/promos`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      code: formData.code.toUpperCase(),
      description: formData.description,
      discount: parseFloat(formData.discount),
      type: formData.type,
      expires: formData.expires
    })
  });

  onUpdate();
  onClose();
};


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Add New Promotion</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input type="text" placeholder="Promo Code" required
            value={formData.code}
            onChange={(e) => setFormData({...formData, code: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
          />
          <input type="text" placeholder="Description" required
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
          />
          <div className="grid grid-cols-2 gap-4">
            <input type="number" step="0.01" placeholder="Discount" required
              value={formData.discount}
              onChange={(e) => setFormData({...formData, discount: e.target.value})}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
            />
            <select value={formData.type}
              onChange={(e) => setFormData({...formData, type: e.target.value})}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
            >
              <option value="percent">Percent</option>
              <option value="fixed">Fixed ($)</option>
            </select>
          </div>
          <input type="date" required
            value={formData.expires}
            onChange={(e) => setFormData({...formData, expires: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
          />
          <div className="flex space-x-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" className="flex-1 px-4 py-2 bg-[#1B2D5C] text-white rounded-lg hover:bg-[#0f1e3d]">Add Promo</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// =========================
// Add Notice Modal
// =========================
const AddNoticeModal = ({ onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    priority: "medium",
    date: new Date().toISOString().split("T")[0],
    emailCustomers: false
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
      await fetch(`/api/notices`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: formData.title,
          message: formData.message,
          priority: formData.priority,
          date: formData.date,
          emailCustomers: formData.emailCustomers
        })
      });

      if (formData.emailCustomers) {
        safeShowAlert('Notice created and emails are being sent to customers!');
      }

      onUpdate();
      onClose();
    } catch (err) {
      safeShowAlert('Error creating notice: ' + err.message);
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Add Service Notice</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            placeholder="Title"
            required
            value={formData.title}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
          />

          <textarea
            placeholder="Message"
            required
            rows="3"
            value={formData.message}
            onChange={(e) =>
              setFormData({ ...formData, message: e.target.value })
            }
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
          />

          <div className="grid grid-cols-2 gap-4">
            <select
              value={formData.priority}
              onChange={(e) =>
                setFormData({ ...formData, priority: e.target.value })
              }
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>

            <input
              type="date"
              value={formData.date}
              onChange={(e) =>
                setFormData({ ...formData, date: e.target.value })
              }
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div className="flex items-center space-x-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <input
              type="checkbox"
              id="emailCustomers"
              checked={formData.emailCustomers}
              onChange={(e) =>
                setFormData({ ...formData, emailCustomers: e.target.checked })
              }
              className="w-4 h-4 text-[#1B2D5C] rounded focus:ring-green-500"
            />
            <label htmlFor="emailCustomers" className="text-sm text-gray-700">
              <Mail className="w-4 h-4 inline mr-1" />
              Email this notice to all customers
            </label>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={sending}
              className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={sending}
              className="flex-1 px-4 py-2 bg-[#1B2D5C] text-white rounded-lg hover:bg-[#0f1e3d] disabled:opacity-50"
            >
              {sending ? 'Creating...' : 'Add Notice'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


// =========================
// Export the Main Component
// =========================
// Add Service to Customer Modal
const AddServiceToCustomerModal = ({ customer, services, locations, onClose, onSuccess }) => {
  const [selectedService, setSelectedService] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [customPrice, setCustomPrice] = useState('');
  const [saving, setSaving] = useState(false);
  const [showChargeActionModal, setShowChargeActionModal] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedService) {
      safeShowAlert('Please select a service');
      return;
    }

    // Show charge action modal
    setShowChargeActionModal(true);
  };

  const handleChargeActionSelect = async (chargeAction) => {
    setShowChargeActionModal(false);
    setSaving(true);

    try {
      // Calculate next billing as 5th of current month (if before 5th) or next month (if on/after 5th)
      const now = new Date();
      const currentDay = now.getDate();
      const monthOffset = currentDay < 5 ? 0 : 1;
      const nextBillingDate = new Date(now.getFullYear(), now.getMonth() + monthOffset, 5);

      const newSub = {
        id: `sub${Date.now()}`,
        customerId: customer.id,
        serviceId: selectedService.id,
        locationId: selectedLocation || null,
        startDate: new Date().toISOString().split('T')[0],
        status: 'active',
        nextBilling: nextBillingDate.toISOString().split('T')[0],
        customPrice: customPrice ? parseFloat(customPrice) : null,
        chargeAction
      };

      const res = await fetch(`/api/subscriptions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newSub)
      });

      if (!res.ok) throw new Error('Failed to add service');

      const actionMsg = chargeAction === 'immediate' ? 'and charged immediately' :
                        chargeAction === 'next_cycle' ? 'and will be charged on the 5th' :
                        'without charging';
      safeShowAlert(`Service added ${actionMsg}!`);
      onSuccess();
    } catch (err) {
      safeShowAlert('Error: ' + err.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Add Service to {customer.name}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Service *</label>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {services.map(service => (
                <button
                  key={service.id}
                  type="button"
                  onClick={() => setSelectedService(service)}
                  className={`w-full text-left px-4 py-3 border-2 rounded-lg transition ${
                    selectedService?.id === service.id
                      ? 'border-[#1B2D5C] bg-[#eef1fa]'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium">{service.name}</p>
                      <p className="text-sm text-gray-600">{service.description}</p>
                    </div>
                    <p className="text-lg font-semibold text-[#1B2D5C]">${service.price}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {locations.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Service Location (Optional)
              </label>
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
              >
                <option value="">Primary Address ({customer.address})</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>
                    {loc.label || loc.address} - {loc.address}, {loc.city}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Custom Price (Optional)
            </label>
            <input
              type="number"
              step="0.01"
              placeholder={selectedService ? `Default: $${selectedService.price}` : 'Enter custom price'}
              value={customPrice}
              onChange={(e) => setCustomPrice(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
            />
            <p className="text-xs text-gray-500 mt-1">Leave blank to use default service price</p>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !selectedService}
              className="px-4 py-2 bg-[#1B2D5C] text-white rounded-lg hover:bg-[#0f1e3d] disabled:opacity-50"
            >
              {saving ? 'Adding...' : 'Add Service'}
            </button>
          </div>
        </form>

        {/* Charge Action Modal */}
        {showChargeActionModal && (
          <ChargeActionModal
            onClose={() => setShowChargeActionModal(false)}
            onSelect={handleChargeActionSelect}
          />
        )}
      </div>
    </div>
  );
};

// Payment Method Setup Form (inside Stripe Elements)
const PaymentMethodForm = ({ customerId, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [paymentType, setPaymentType] = useState('card');
  const [accountHolderName, setAccountHolderName] = useState('');
  const [accountHolderEmail, setAccountHolderEmail] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || (paymentType === 'card' && !elements)) {
      return;
    }

    setProcessing(true);
    setError('');

    try {
      // Get setup intent client secret
      const setupRes = await fetch(`/api/stripe/setup-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify({ customerId })
      });

      if (!setupRes.ok) {
        const errorData = await setupRes.json();
        throw new Error(errorData.error || 'Failed to create setup intent');
      }

      const responseData = await setupRes.json();
      const clientSecret = responseData.clientSecret;

      if (!clientSecret) {
        throw new Error('No client secret received from server');
      }

      let setupIntent;
      let stripeError;

      if (paymentType === 'card') {
        // Confirm card setup
        const result = await stripe.confirmCardSetup(clientSecret, {
          payment_method: {
            card: elements.getElement(CardElement),
          }
        });
        setupIntent = result.setupIntent;
        stripeError = result.error;
      } else {
        // Collect bank account for setup
        const result = await stripe.collectBankAccountForSetup({
          clientSecret: clientSecret,
          params: {
            payment_method_type: 'us_bank_account',
            payment_method_data: {
              billing_details: {
                name: accountHolderName,
                email: accountHolderEmail,
              },
            },
          },
          expand: ['payment_method'],
        });
        setupIntent = result.setupIntent;
        stripeError = result.error;
      }

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      if (setupIntent.status === 'requires_payment_method') {
        throw new Error('Bank account verification was cancelled. Please try again.');
      }

      // Extract payment method ID (may be an object if expanded)
      const paymentMethodId = typeof setupIntent.payment_method === 'string'
        ? setupIntent.payment_method
        : setupIntent.payment_method.id;

      // Save payment method
      const saveRes = await fetch(`/api/stripe/save-payment-method`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify({
          customerId,
          paymentMethodId: paymentMethodId
        })
      });

      if (!saveRes.ok) {
        const data = await saveRes.json();
        throw new Error(data.error);
      }

      onSuccess();
    } catch (err) {
      setError(err.message);
      setProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Payment Method Type
        </label>
        <div className="flex space-x-4">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="radio"
              value="card"
              checked={paymentType === 'card'}
              onChange={(e) => setPaymentType(e.target.value)}
              className="w-4 h-4 text-[#1B2D5C]"
            />
            <span className="text-sm">Credit/Debit Card</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="radio"
              value="bank"
              checked={paymentType === 'bank'}
              onChange={(e) => setPaymentType(e.target.value)}
              className="w-4 h-4 text-[#1B2D5C]"
            />
            <span className="text-sm">Bank Account (ACH)</span>
          </label>
        </div>
      </div>

      {paymentType === 'card' ? (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Card Information
          </label>
          <div className="p-3 border rounded-lg">
            <CardElement options={cardElementOptions} />
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Account Holder Name
            </label>
            <input
              type="text"
              value={accountHolderName}
              onChange={(e) => setAccountHolderName(e.target.value)}
              required
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
              placeholder="John Doe"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={accountHolderEmail}
              onChange={(e) => setAccountHolderEmail(e.target.value)}
              required
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
              placeholder="john@example.com"
            />
          </div>
          <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="font-medium mb-1">ACH Direct Debit</p>
            <p>Click "Save Payment Method" to securely link your bank account through Stripe. You'll be able to instantly verify your account or use microdeposits.</p>
          </div>
        </div>
      )}

      {error && (
        <div className="text-sm text-red-700 bg-red-100 rounded p-3">
          {error}
        </div>
      )}

      <div className="flex justify-end space-x-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          disabled={processing}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || processing || (paymentType === 'bank' && (!accountHolderName || !accountHolderEmail))}
          className="px-4 py-2 bg-[#1B2D5C] text-white rounded-lg hover:bg-[#0f1e3d] disabled:opacity-50"
        >
          {processing ? 'Processing...' : 'Save Payment Method'}
        </button>
      </div>
    </form>
  );
};

// Proration Confirmation Modal
const ProrationConfirmationModal = ({ prorationData, onConfirm, onCancel, isPartial }) => {
  const data = isPartial ? prorationData.proration : prorationData.full;
  const chargeType = isPartial ? 'Prorated Charge' : 'Full Charge';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl animate-scale-in">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Confirm {chargeType}</h2>
          <p className="text-gray-600">
            Review the charge details before processing:
          </p>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 mb-6 space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-gray-700 font-medium">Service:</span>
            <span className="text-gray-900 font-semibold">{prorationData.serviceName}</span>
          </div>

          {isPartial && (
            <>
              <div className="flex justify-between items-center">
                <span className="text-gray-700 font-medium">Full Price:</span>
                <span className="text-gray-500 line-through">${prorationData.servicePrice.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700 font-medium">Proration:</span>
                <span className="text-blue-700 font-semibold">
                  {data.prorationPercent}% ({data.weeksRemaining} week{data.weeksRemaining !== 1 ? 's' : ''} remaining)
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700 font-medium">Prorated Amount:</span>
                <span className="text-gray-900 font-semibold">${data.proratedAmount.toFixed(2)}</span>
              </div>
            </>
          )}

          {!isPartial && (
            <div className="flex justify-between items-center">
              <span className="text-gray-700 font-medium">Service Amount:</span>
              <span className="text-gray-900 font-semibold">${data.amount.toFixed(2)}</span>
            </div>
          )}

          <div className="border-t border-gray-300 pt-3">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">Processing Fee ({data.processingFeePercent}%):</span>
              <span className="text-gray-700">${data.processingFee.toFixed(2)}</span>
            </div>
          </div>

          <div className="flex justify-between items-center pt-2 border-t-2 border-gray-400">
            <span className="text-lg font-bold text-gray-900">Total to Charge:</span>
            <span className="text-lg font-bold text-[#0f1e3d]">${data.totalWithFee.toFixed(2)}</span>
          </div>

          {isPartial && (
            <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-200">
              <p className="text-xs text-blue-800">
                <strong>Note:</strong> Next billing on {data.nextBillingDate} will be at full price (${prorationData.servicePrice.toFixed(2)}).
              </p>
            </div>
          )}
        </div>

        <div className="flex space-x-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-2 bg-[#1B2D5C] text-white rounded-lg hover:bg-[#0f1e3d] font-medium"
          >
            Confirm & Charge
          </button>
        </div>
      </div>
    </div>
  );
};

// Charge Action Selection Modal
const ChargeActionModal = ({ requestId, onClose, onSelect }) => {
  const [loading, setLoading] = useState(false);
  const [prorationData, setProrationData] = useState(null);
  const [showProrationConfirm, setShowProrationConfirm] = useState(null); // 'full' or 'partial'
  const [pickupDay, setPickupDay] = useState('');

  // If no requestId, use simple mode (for AddServiceToCustomerModal - backward compatibility)
  const simpleMode = !requestId;

  const handleOptionClick = async (option) => {
    if (simpleMode) {
      // Simple mode: map new values to old values for backward compatibility
      const legacyValue = option === 'full' ? 'immediate' : option === 'partial' ? 'next_cycle' : 'skip';
      onSelect(legacyValue);
      return;
    }

    if (option === 'skip') {
      // Skip directly without proration calculation
      onSelect('skip', pickupDay);
      return;
    }

    // For 'full' or 'partial', fetch proration data first
    setLoading(true);
    try {
      const res = await fetch(`/api/service-requests/${requestId}/calculate-proration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.error || 'Failed to calculate proration');

      setProrationData(data);
      setShowProrationConfirm(option);
    } catch (err) {
      safeShowAlert('Error calculating proration: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleProrationConfirm = () => {
    onSelect(showProrationConfirm, pickupDay);
    setShowProrationConfirm(null);
  };

  const handleProrationCancel = () => {
    setShowProrationConfirm(null);
  };

  if (showProrationConfirm && prorationData) {
    return (
      <ProrationConfirmationModal
        prorationData={prorationData}
        onConfirm={handleProrationConfirm}
        onCancel={handleProrationCancel}
        isPartial={showProrationConfirm === 'partial'}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-2xl animate-scale-in">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Billing Option</h2>
          <p className="text-gray-600">
            {simpleMode
              ? 'Choose how you want to handle the first payment for this service:'
              : 'Choose how you want to handle the payment for this service:'
            }
          </p>
        </div>

        {!simpleMode && (
          <div className="mb-5">
            <label className="block text-sm font-semibold text-gray-700 mb-1">Pickup Day</label>
            <select
              value={pickupDay}
              onChange={(e) => setPickupDay(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- Select day --</option>
              <option value="Monday">Monday</option>
              <option value="Tuesday">Tuesday</option>
              <option value="Wednesday">Wednesday</option>
              <option value="Thursday">Thursday</option>
              <option value="Friday">Friday</option>
            </select>
          </div>
        )}

        <div className="space-y-3 mb-6">
          <button
            onClick={() => handleOptionClick('full')}
            disabled={loading}
            className="w-full text-left p-4 bg-[#eef1fa] border-2 border-green-200 rounded-lg hover:bg-[#dce4f5] hover:border-green-300 transition disabled:opacity-50"
          >
            <div className="flex items-start">
              <DollarSign className="w-6 h-6 text-[#1B2D5C] mr-3 mt-0.5" />
              <div>
                <h3 className="font-semibold text-green-900 mb-1">
                  {simpleMode ? 'Charge Immediately' : 'Charge Full Amount'}
                </h3>
                <p className="text-sm text-[#0f1e3d]">
                  {simpleMode
                    ? 'Process payment right now for the first month of service.'
                    : 'Create invoice and charge the full monthly service price immediately.'
                  }
                </p>
              </div>
            </div>
          </button>

          <button
            onClick={() => handleOptionClick('partial')}
            disabled={loading}
            className="w-full text-left p-4 bg-blue-50 border-2 border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 transition disabled:opacity-50"
          >
            <div className="flex items-start">
              <Calendar className="w-6 h-6 text-blue-600 mr-3 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-1">
                  {simpleMode ? 'Wait Until Next Bill Cycle' : 'Partial Charge (Prorated)'}
                </h3>
                <p className="text-sm text-blue-700">
                  {simpleMode
                    ? "Don't charge now. Customer will be billed on the 5th of next month."
                    : 'Create invoice and charge prorated amount based on weeks remaining until the 5th.'
                  }
                </p>
              </div>
            </div>
          </button>

          <button
            onClick={() => handleOptionClick('skip')}
            disabled={loading}
            className="w-full text-left p-4 bg-gray-50 border-2 border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition disabled:opacity-50"
          >
            <div className="flex items-start">
              <XCircle className="w-6 h-6 text-gray-600 mr-3 mt-0.5" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Don't Charge{simpleMode ? '' : ' Now'}</h3>
                <p className="text-sm text-gray-700">
                  {simpleMode
                    ? 'Add service without charging. Useful for complimentary services.'
                    : 'Approve service without charging. Will be billed at full price on the 5th.'
                  }
                </p>
              </div>
            </div>
          </button>
        </div>

        <div className="flex justify-end">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
};


// Reports Tab
const ReportsTab = () => {
  const [activeReport, setActiveReport] = useState('summary');
  const [summary, setSummary] = useState(null);
  const [pastDue, setPastDue] = useState(null);
  const [activeCustomers, setActiveCustomers] = useState(null);
  const [autopay, setAutopay] = useState(null);
  const [allCustomers, setAllCustomers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pastDueDays, setPastDueDays] = useState(30);
  const [services, setServices] = useState([]);
  const [statusFilter, setStatusFilter] = useState('active');
  const [serviceFilter, setServiceFilter] = useState('');

  useEffect(() => { loadSummary(); loadServices(); }, []);
  useEffect(() => {
    if (activeReport === 'past-due') loadPastDue();
    if (activeReport === 'active-customers') loadActiveCustomers();
    if (activeReport === 'autopay') loadAutopay();
    if (activeReport === 'all-customers') loadAllCustomers();
  }, [activeReport, pastDueDays, statusFilter, serviceFilter]);

  const loadServices = async () => {
    try {
      const res = await fetch('/api/services');
      setServices(await res.json());
    } catch (e) { console.error(e); }
  };

  const filterParams = () => {
    const p = new URLSearchParams();
    if (statusFilter && statusFilter !== 'all') p.set('status', statusFilter);
    if (serviceFilter) p.set('serviceId', serviceFilter);
    return p;
  };

  const loadSummary = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/reports/summary');
      setSummary(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadPastDue = async () => {
    setLoading(true);
    try {
      const params = filterParams();
      params.set('days', pastDueDays);
      const res = await fetch(`/api/reports/past-due?${params.toString()}`);
      setPastDue(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadActiveCustomers = async () => {
    setLoading(true);
    try {
      const params = filterParams();
      const res = await fetch(`/api/reports/active-customers?${params.toString()}`);
      setActiveCustomers(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadAutopay = async () => {
    setLoading(true);
    try {
      const params = filterParams();
      const res = await fetch(`/api/reports/autopay?${params.toString()}`);
      setAutopay(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadAllCustomers = async () => {
    setLoading(true);
    try {
      const params = filterParams();
      const res = await fetch(`/api/reports/all-customers?${params.toString()}`);
      setAllCustomers(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const downloadCSV = (baseUrl, filename, extraParams = {}) => {
    const params = filterParams();
    Object.entries(extraParams).forEach(([k, v]) => params.set(k, v));
    const a = document.createElement('a');
    a.href = `${baseUrl}?${params.toString()}`;
    a.download = filename;
    a.click();
  };

  const tabs = [
    { id: 'summary', label: 'Summary' },
    { id: 'past-due', label: 'Past Due' },
    { id: 'active-customers', label: 'Active Customers' },
    { id: 'autopay', label: 'AutoPay' },
    { id: 'all-customers', label: 'All Customers' },
  ];

  const showFilters = activeReport !== 'summary';

  return (
    <div className="space-y-6">
      {/* Sub-nav */}
      <div className="flex space-x-2 border-b pb-0">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveReport(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition -mb-px ${
              activeReport === t.id ? 'border-[#1B2D5C] text-[#1B2D5C]' : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >{t.label}</button>
        ))}
      </div>

      {/* Shared filters */}
      {showFilters && (
        <div className="flex flex-wrap items-center gap-3 bg-white rounded-xl shadow p-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Status</label>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Service Type</label>
            <select
              value={serviceFilter}
              onChange={e => setServiceFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
            >
              <option value="">All Services</option>
              {services.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-200 border-t-[#1B2D5C]" />
        </div>
      )}

      {/* Summary */}
      {activeReport === 'summary' && summary && !loading && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow p-5">
              <p className="text-sm text-gray-500">Total Customers</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{summary.totalCustomers}</p>
              <p className="text-xs text-gray-400 mt-1">Active: {summary.activeCustomers} &middot; Inactive: {summary.inactiveCustomers}</p>
            </div>
            <div className="bg-white rounded-xl shadow p-5">
              <p className="text-sm text-gray-500">Revenue This Month</p>
              <p className="text-3xl font-bold text-green-600 mt-1">${Number(summary.revenueThisMonth).toFixed(2)}</p>
            </div>
            <div className="bg-white rounded-xl shadow p-5">
              <p className="text-sm text-gray-500">Total Unpaid</p>
              <p className="text-3xl font-bold text-yellow-600 mt-1">${Number(summary.unpaidTotal).toFixed(2)}</p>
              <p className="text-xs text-gray-400 mt-1">{summary.unpaidCount} invoices</p>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {[
              { label: 'Past Due 30+ Days', count: summary.pastDue30Count, total: summary.pastDue30Total, color: 'yellow' },
              { label: 'Past Due 60+ Days', count: summary.pastDue60Count, total: summary.pastDue60Total, color: 'orange' },
              { label: 'Past Due 90+ Days', count: summary.pastDue90Count, total: summary.pastDue90Total, color: 'red' },
            ].map(b => (
              <div key={b.label} className={`bg-white rounded-xl shadow p-5 border-l-4 border-${b.color}-400`}>
                <p className={`text-sm font-semibold text-${b.color}-700`}>{b.label}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{b.count} customers</p>
                <p className={`text-lg font-semibold text-${b.color}-600`}>${Number(b.total).toFixed(2)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Past Due */}
      {activeReport === 'past-due' && !loading && (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Show past due</label>
              <select
                value={pastDueDays}
                onChange={e => setPastDueDays(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
              >
                <option value={30}>30+ days</option>
                <option value={45}>45+ days</option>
                <option value={60}>60+ days</option>
                <option value={90}>90+ days</option>
              </select>
            </div>
            <button
              onClick={() => downloadCSV('/api/reports/past-due/csv', `past_due_${pastDueDays}days.csv`, { days: pastDueDays })}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#1B2D5C] text-white text-sm font-medium rounded-lg hover:bg-[#0f1e3d] transition"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>

          {pastDue && (
            <div className="bg-white rounded-xl shadow overflow-hidden">
              <div className="px-5 py-3 border-b bg-gray-50 flex justify-between items-center">
                <span className="font-semibold text-gray-700">{pastDue.count} customers</span>
                <span className="text-sm text-gray-500">Total: ${pastDue.customers ? pastDue.customers.reduce((s, c) => s + c.totalOverdue, 0).toFixed(2) : '0.00'}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                    <tr>
                      <th className="px-4 py-3 text-left">Customer</th>
                      <th className="px-4 py-3 text-left">Phone</th>
                      <th className="px-4 py-3 text-left">Pickup Day</th>
                      <th className="px-4 py-3 text-right">Invoices</th>
                      <th className="px-4 py-3 text-right">Total Owed</th>
                      <th className="px-4 py-3 text-right">Max Days</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {pastDue.customers && pastDue.customers.map(c => (
                      <tr key={c.customerId} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">{c.name}</p>
                          <p className="text-xs text-gray-400">{c.email}</p>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{c.phone || '—'}</td>
                        <td className="px-4 py-3 text-gray-600">{c.pickupDay}</td>
                        <td className="px-4 py-3 text-right text-gray-600">{c.invoices.length}</td>
                        <td className="px-4 py-3 text-right font-semibold text-red-600">${c.totalOverdue.toFixed(2)}</td>
                        <td className="px-4 py-3 text-right">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.maxDaysOverdue >= 90 ? 'bg-red-100 text-red-700' : c.maxDaysOverdue >= 60 ? 'bg-orange-100 text-orange-700' : 'bg-yellow-100 text-yellow-700'}`}>
                            {c.maxDaysOverdue}d
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Active Customers */}
      {activeReport === 'active-customers' && !loading && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => downloadCSV('/api/reports/active-customers/csv', 'active_customers.csv')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#1B2D5C] text-white text-sm font-medium rounded-lg hover:bg-[#0f1e3d] transition"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>
          {activeCustomers && (
            <div className="bg-white rounded-xl shadow overflow-hidden">
              <div className="px-5 py-3 border-b bg-gray-50 flex justify-between items-center">
                <span className="font-semibold text-gray-700">{activeCustomers.count} customers</span>
                <span className="text-sm text-gray-500">Monthly total: ${activeCustomers.customers ? activeCustomers.customers.reduce((s, c) => s + c.totalMonthly, 0).toFixed(2) : '0.00'}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                    <tr>
                      <th className="px-4 py-3 text-left">Customer</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-left">Phone</th>
                      <th className="px-4 py-3 text-left">Pickup Day</th>
                      <th className="px-4 py-3 text-left">Services</th>
                      <th className="px-4 py-3 text-right">Monthly</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {activeCustomers.customers && activeCustomers.customers.map(c => (
                      <tr key={c.customerId} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">{c.name}</p>
                          <p className="text-xs text-gray-400">{c.email}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                            {c.status === 'active' ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{c.phone || '—'}</td>
                        <td className="px-4 py-3 text-gray-600">{c.pickupDay}</td>
                        <td className="px-4 py-3 text-gray-600 text-xs">{c.subscriptions.map(s => s.serviceName).join(', ') || '—'}</td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-900">${c.totalMonthly.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* AutoPay Customers */}
      {activeReport === 'autopay' && !loading && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => downloadCSV('/api/reports/autopay/csv', 'autopay_customers.csv')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#1B2D5C] text-white text-sm font-medium rounded-lg hover:bg-[#0f1e3d] transition"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>
          {autopay && (
            <div className="bg-white rounded-xl shadow overflow-hidden">
              <div className="px-5 py-3 border-b bg-gray-50 flex justify-between items-center">
                <span className="font-semibold text-gray-700">{autopay.count} autopay customers</span>
                <span className="text-sm text-gray-500">Active: {autopay.activeCount} &middot; Inactive: {autopay.inactiveCount}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                    <tr>
                      <th className="px-4 py-3 text-left">Customer</th>
                      <th className="px-4 py-3 text-left">Phone</th>
                      <th className="px-4 py-3 text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {autopay.customers && autopay.customers.map(c => (
                      <tr key={c.customerId} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">{c.name}</p>
                          <p className="text-xs text-gray-400">{c.email}</p>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{c.phone || '—'}</td>
                        <td className="px-4 py-3 text-right">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                            {c.status === 'active' ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* All Customers — services + account credit */}
      {activeReport === 'all-customers' && !loading && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => downloadCSV('/api/reports/all-customers/csv', 'all_customers.csv')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#1B2D5C] text-white text-sm font-medium rounded-lg hover:bg-[#0f1e3d] transition"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>
          {allCustomers && (
            <div className="bg-white rounded-xl shadow overflow-hidden">
              <div className="px-5 py-3 border-b bg-gray-50 flex justify-between items-center">
                <span className="font-semibold text-gray-700">{allCustomers.count} customers</span>
                <span className="text-sm text-gray-500">Total account credit: ${Number(allCustomers.totalCredit || 0).toFixed(2)}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                    <tr>
                      <th className="px-4 py-3 text-left">Customer</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-left">Subscribed Services</th>
                      <th className="px-4 py-3 text-right">Account Credit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {allCustomers.customers && allCustomers.customers.map(c => (
                      <tr key={c.customerId} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">{c.name}</p>
                          <p className="text-xs text-gray-400">{c.email}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                            {c.status === 'active' ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-xs">{c.services.map(s => s.serviceName).join(', ') || '—'}</td>
                        <td className={`px-4 py-3 text-right font-semibold ${c.credit > 0 ? 'text-green-600' : 'text-gray-900'}`}>${Number(c.credit || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// AutoPay Enrollment Modal
// AutoPay Enrollment Modal
const AutoPayEnrollmentModal = ({ customerId, onClose, onEnroll }) => {
  const [enrolling, setEnrolling] = useState(false);

  const handleEnroll = async (enable) => {
    setEnrolling(true);
    try {
      const res = await fetch(`/api/customers/${customerId}/autopay`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify({ autoPayEnabled: enable })
      });

      if (!res.ok) throw new Error('Failed to update autopay');

      onEnroll(enable);
      onClose();
    } catch (err) {
      console.error('[AUTOPAY ENROLLMENT ERROR]', err);
      showAlert('Error updating autopay settings. Please try again.');
    } finally {
      setEnrolling(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-2xl animate-scale-in">
        <div className="flex items-start space-x-4 mb-6">
          <div className="w-12 h-12 rounded-full bg-[#dce4f5] flex items-center justify-center flex-shrink-0">
            <CreditCard className="w-6 h-6 text-[#1B2D5C]" />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Enable AutoPay?</h2>
            <p className="text-gray-600">
              Automatically pay your monthly bills on the 5th of each month using your saved payment method.
            </p>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-blue-900 mb-2">Benefits of AutoPay:</h3>
          <ul className="space-y-2 text-sm text-blue-800">
            <li className="flex items-start">
              <Check className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
              <span>Never miss a payment</span>
            </li>
            <li className="flex items-start">
              <Check className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
              <span>Avoid late fees</span>
            </li>
            <li className="flex items-start">
              <Check className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
              <span>Save time - no manual payments</span>
            </li>
            <li className="flex items-start">
              <Check className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
              <span>You can disable it anytime</span>
            </li>
          </ul>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-gray-700">
            <strong>Note:</strong> You'll receive an email confirmation before each charge.
            You can toggle AutoPay on/off anytime in your account settings.
          </p>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={() => handleEnroll(false)}
            disabled={enrolling}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50"
          >
            {enrolling ? 'Processing...' : 'No Thanks'}
          </button>
          <button
            onClick={() => handleEnroll(true)}
            disabled={enrolling}
            className="px-6 py-2 bg-[#1B2D5C] text-white rounded-lg hover:bg-[#0f1e3d] disabled:opacity-50 flex items-center space-x-2"
          >
            <Check className="w-5 h-5" />
            <span>{enrolling ? 'Enrolling...' : 'Enable AutoPay'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Payment Method Setup Modal
const PaymentMethodSetupModal = ({ customerId, onClose, onSuccess }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">Add Payment Method</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-600">
            Add a payment method to enable automatic billing for your services.
          </p>
        </div>

        <Elements stripe={stripePromise}>
          <PaymentMethodForm
            customerId={customerId}
            onSuccess={() => {
              onSuccess();
              onClose();
            }}
            onCancel={onClose}
          />
        </Elements>
      </div>
    </div>
  );
};

// Payment Method Display Component
const PaymentMethodDisplay = ({ customerId }) => {
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);

  const loadPaymentMethod = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/stripe/payment-method/${customerId}`, {
        headers: { Authorization: `Bearer ${auth.getToken()}` }
      });
      const data = await res.json();
      if (data.hasPaymentMethod) {
        setPaymentMethod(data.paymentMethod);
      }
    } catch (err) {
      console.error('Failed to load payment method:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadPaymentMethod();
  }, [customerId]);

  if (loading) {
    return <div className="text-sm text-gray-500">Loading...</div>;
  }

  return (
    <div className="bg-white rounded-xl shadow p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Payment Method</h3>
        {!paymentMethod && (
          <button
            onClick={() => setShowSetup(true)}
            className="text-[#1B2D5C] hover:text-[#0f1e3d] text-sm font-medium"
          >
            + Add Card
          </button>
        )}
      </div>

      {paymentMethod ? (
        <div className="flex items-center space-x-3">
          <div className="w-12 h-8 bg-gray-100 rounded flex items-center justify-center">
            {paymentMethod.type === 'us_bank_account' ? (
              <Building className="w-6 h-6 text-gray-600" />
            ) : (
              <CreditCard className="w-6 h-6 text-gray-600" />
            )}
          </div>
          <div>
            {paymentMethod.type === 'us_bank_account' ? (
              <>
                <p className="font-medium">{paymentMethod.bank_name} •••• {paymentMethod.last4}</p>
                <p className="text-sm text-gray-500 capitalize">
                  {paymentMethod.account_type} Account (ACH)
                </p>
              </>
            ) : (
              <>
                <p className="font-medium capitalize">{paymentMethod.brand} •••• {paymentMethod.last4}</p>
                <p className="text-sm text-gray-500">
                  Expires {paymentMethod.exp_month}/{paymentMethod.exp_year}
                </p>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 mb-2">No payment method on file</p>
          <p className="text-sm text-gray-500 mb-4">Add a card to enable automatic billing</p>
          <button
            onClick={() => setShowSetup(true)}
            className="bg-[#1B2D5C] text-white px-4 py-2 rounded-lg hover:bg-[#0f1e3d]"
          >
            Add Payment Method
          </button>
        </div>
      )}

      {showSetup && (
        <PaymentMethodSetupModal
          customerId={customerId}
          onClose={() => setShowSetup(false)}
          onSuccess={loadPaymentMethod}
        />
      )}
    </div>
  );
};

// Add Location Modal
const AddLocationModal = ({ customerId, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    label: '',
    address: '',
    city: '',
    zip: '',
    isPrimary: false
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`/api/locations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId,
          ...formData
        })
      });
      if (!res.ok) throw new Error('Failed to create location');
      onSuccess();
    } catch (err) {
      safeShowAlert('Error: ' + err.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Add Service Location</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Location Label (Optional)
            </label>
            <input
              type="text"
              placeholder="e.g., Main Office, Warehouse"
              value={formData.label}
              onChange={(e) => setFormData({...formData, label: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address *
            </label>
            <input
              type="text"
              required
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({...formData, city: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
              <input
                type="text"
                value={formData.zip}
                onChange={(e) => setFormData({...formData, zip: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
              />
            </div>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isPrimary"
              checked={formData.isPrimary}
              onChange={(e) => setFormData({...formData, isPrimary: e.target.checked})}
              className="w-4 h-4 text-[#1B2D5C] border-gray-300 rounded focus:ring-green-500"
            />
            <label htmlFor="isPrimary" className="ml-2 text-sm text-gray-700">
              Set as primary location
            </label>
          </div>
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-[#1B2D5C] text-white rounded-lg hover:bg-[#0f1e3d] disabled:opacity-50"
            >
              {saving ? 'Adding...' : 'Add Location'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Record Payment Modal (for manual payment entry)
const RecordPaymentModal = ({ invoice, customer, onClose, onSuccess }) => {
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [amount, setAmount] = useState(invoice.amount);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [showCardForm, setShowCardForm] = useState(false);

  const handleOfflinePayment = async () => {
    setProcessing(true);
    setError('');

    try {
      const res = await fetch(`/api/stripe/record-offline-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify({
          customerId: customer.id,
          amount: parseFloat(amount),
          method: paymentMethod,
          invoiceId: invoice.id,
          referenceNumber,
          notes
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error);
      }

      safeShowAlert(`Payment of $${amount} recorded successfully!`);
      onSuccess();
    } catch (err) {
      setError(err.message);
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold">Record Payment</h2>
            <p className="text-sm text-gray-600">Invoice: {invoice.id} - {customer.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-gray-700">Invoice Amount:</span>
            <span className="text-2xl font-bold text-gray-900">${invoice.amount.toFixed(2)}</span>
          </div>
        </div>

        {error && (
          <div className="mb-4 text-sm text-red-700 bg-red-100 rounded p-3">
            {error}
          </div>
        )}

        <div className="space-y-4">
          {/* Payment Method Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: 'cash', label: 'Cash' },
                { value: 'check', label: 'Check' },
                { value: 'card', label: 'Credit/Debit Card' },
                { value: 'wire_transfer', label: 'Wire/ACH' },
                { value: 'money_order', label: 'Money Order' },
                { value: 'other', label: 'Other' }
              ].map(method => (
                <button
                  key={method.value}
                  type="button"
                  onClick={() => {
                    setPaymentMethod(method.value);
                    setShowCardForm(method.value === 'card');
                  }}
                  className={`px-4 py-3 rounded-lg border-2 text-sm font-medium transition ${
                    paymentMethod === method.value
                      ? 'border-[#1B2D5C] bg-[#eef1fa] text-[#0f1e3d]'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {method.label}
                </button>
              ))}
            </div>
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
            <input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
            />
          </div>

          {/* Reference Number (for checks, wire transfers, etc.) */}
          {['check', 'wire_transfer', 'money_order'].includes(paymentMethod) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {paymentMethod === 'check' ? 'Check Number' : 'Reference Number'}
              </label>
              <input
                type="text"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                placeholder={paymentMethod === 'check' ? '1234' : 'Transaction ID'}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
              />
            </div>
          )}

          {/* Manual Card Entry */}
          {showCardForm && (
            <div className="border-t pt-4">
              <p className="text-sm text-gray-600 mb-3">
                Enter card details below to process payment securely via Stripe.
              </p>
              <Elements stripe={stripePromise}>
                <ManualCardPaymentForm
                  customerId={customer.id}
                  amount={parseFloat(amount)}
                  invoiceId={invoice.id}
                  onSuccess={onSuccess}
                  onCancel={() => setShowCardForm(false)}
                />
              </Elements>
            </div>
          )}

          {/* Notes */}
          {!showCardForm && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes (Optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional payment details..."
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
                rows="3"
              />
            </div>
          )}

          {/* Action Buttons (only for non-card payments) */}
          {!showCardForm && (
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                disabled={processing}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleOfflinePayment}
                disabled={processing || !amount}
                className="px-4 py-2 bg-[#1B2D5C] text-white rounded-lg hover:bg-[#0f1e3d] disabled:opacity-50"
              >
                {processing ? 'Recording...' : 'Record Payment'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Manual Card Payment Form (inside Stripe Elements)
const ManualCardPaymentForm = ({ customerId, amount, invoiceId, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [cardFee, setCardFee] = useState(0);

  // Load processing fee settings
  useEffect(() => {
    const loadFees = async () => {
      try {
        const res = await fetch('/api/settings');
        const settings = await res.json();
        setCardFee(parseFloat(settings.card_processing_fee || '0'));
      } catch (err) {
        console.error('Failed to load processing fees:', err);
      }
    };
    loadFees();
  }, []);

  const processingFee = amount * (cardFee / 100);
  const totalAmount = amount + processingFee;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError('');

    try {
      // Create payment method from card
      const { paymentMethod, error: stripeError } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
      });

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      // Charge using the payment method
      const res = await fetch(`/api/stripe/charge-manual-card`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify({
          customerId,
          amount,
          invoiceId,
          cardToken: paymentMethod.id,
          description: `Manual payment for invoice ${invoiceId}`
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error);
      }

      const feeMsg = data.processingFee > 0 ? ` (Processing fee: $${data.processingFee.toFixed(2)} included)` : '';
      safeShowAlert(`Payment of $${amount} processed successfully!${feeMsg}`);
      onSuccess();
    } catch (err) {
      setError(err.message);
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Card Information
        </label>
        <div className="p-3 border rounded-lg">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': { color: '#aab7c4' },
                },
                invalid: { color: '#9e2146' },
              },
            }}
          />
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-700 bg-red-100 rounded p-3">
          {error}
        </div>
      )}

      {/* Fee Breakdown */}
      {cardFee > 0 && (
        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
          <div className="text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-gray-600">Invoice amount:</span>
              <span className="font-medium">${amount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Processing fee ({cardFee}%):</span>
              <span className="font-medium">${processingFee.toFixed(2)}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-blue-300">
              <span className="font-semibold text-gray-900">Total to charge:</span>
              <span className="font-semibold text-gray-900">${totalAmount.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end space-x-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          disabled={processing}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || processing}
          className="px-4 py-2 bg-[#1B2D5C] text-white rounded-lg hover:bg-[#0f1e3d] disabled:opacity-50"
        >
          {processing ? 'Processing...' : `Charge $${totalAmount.toFixed(2)}`}
        </button>
      </div>
    </form>
  );
};


const AddressConfirmDialog = ({ current, suggested, onUse, onKeep }) => (
  <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-4">
    <div className="bg-white rounded-xl max-w-md w-full p-6 space-y-4 shadow-2xl">
      <h3 className="text-lg font-semibold flex items-center gap-2">
        <MapPin className="w-5 h-5 text-[#1B2D5C]" />
        Address Suggestion
      </h3>
      <p className="text-sm text-gray-600">We found a standardized version of your address from the US Census Bureau:</p>
      <div className="space-y-3">
        <div className="border rounded-lg p-3 bg-gray-50">
          <p className="text-xs font-medium text-gray-500 mb-1">You entered:</p>
          <p className="text-sm">
            {current.address}{current.city ? `, ${current.city}` : ''}{current.zip ? ` ${current.zip}` : ''}
          </p>
        </div>
        <div className="border-2 border-green-500 rounded-lg p-3 bg-green-50">
          <p className="text-xs font-medium text-green-700 mb-1">✓ Standardized:</p>
          <p className="text-sm font-medium text-green-900">{suggested.full}</p>
        </div>
      </div>
      <div className="flex gap-3 pt-1">
        <button onClick={onKeep} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
          Keep mine
        </button>
        <button onClick={onUse} className="flex-1 px-4 py-2 bg-[#1B2D5C] text-white rounded-lg text-sm font-semibold hover:bg-[#0f1e3d]">
          Use standardized
        </button>
      </div>
    </div>
  </div>
);

const AddressReviewTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [filter, setFilter] = useState('pending');
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({ address: '', city: '', zip: '' });

  const authHeader = () => ({ Authorization: `Bearer ${auth.getToken()}` });

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/address/review', { headers: authHeader() });
      if (res.ok) setItems(await res.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const [jobStatus, setJobStatus] = useState(null);

  const pollJob = async () => {
    try {
      const res = await fetch('/api/address/bulk-status', { headers: authHeader() });
      if (!res.ok) return;
      const job = await res.json();
      setJobStatus(job);
      if (job.running) {
        setTimeout(pollJob, 2000);
      } else {
        setValidating(false);
        await load();
        if (job.error) {
          alert('Validation error: ' + job.error);
        }
      }
    } catch (_) {
      setTimeout(pollJob, 3000);
    }
  };

  const runBulkValidation = async () => {
    if (!window.confirm('This will call the US Census API for every customer address. This may take a few minutes depending on how many customers you have. Continue?')) return;
    setValidating(true);
    setJobStatus(null);
    try {
      const res = await fetch('/api/address/bulk-validate', { method: 'POST', headers: authHeader() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to start');
      if (!data.started) {
        alert(data.message || 'Already running');
      }
      // Start polling for progress
      setTimeout(pollJob, 1500);
    } catch (e) {
      alert('Error: ' + e.message);
      setValidating(false);
    }
  };

  const approve = async (customerId) => {
    const res = await fetch(`/api/address/review/${customerId}/approve`, { method: 'PATCH', headers: authHeader() });
    if (res.ok) setItems(prev => prev.map(i => i.customerId === customerId
      ? { ...i, status: 'approved', currentAddress: i.suggestedAddress, currentCity: i.suggestedCity, currentZip: i.suggestedZip }
      : i));
  };

  const skip = async (customerId) => {
    const res = await fetch(`/api/address/review/${customerId}/skip`, { method: 'PATCH', headers: authHeader() });
    if (res.ok) setItems(prev => prev.map(i => i.customerId === customerId ? { ...i, status: 'skipped' } : i));
  };

  const applyManual = async (customerId) => {
    const res = await fetch(`/api/address/review/${customerId}/manual`, {
      method: 'PATCH',
      headers: { ...authHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(editValues),
    });
    if (res.ok) {
      setItems(prev => prev.map(i => i.customerId === customerId
        ? { ...i, status: 'manual', currentAddress: editValues.address, currentCity: editValues.city, currentZip: editValues.zip }
        : i));
      setEditingId(null);
    }
  };

  const counts = {
    pending: items.filter(i => i.status === 'pending').length,
    approved: items.filter(i => i.status === 'approved').length,
    skipped: items.filter(i => i.status === 'skipped').length,
    manual: items.filter(i => i.status === 'manual').length,
  };
  const filtered = filter === 'all' ? items : items.filter(i => i.status === filter);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold">Address Review</h2>
          <p className="text-sm text-gray-500 mt-1">Validate and standardize customer addresses using the free US Census Geocoder.</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <button
            onClick={runBulkValidation}
            disabled={validating}
            className="px-4 py-2 bg-[#1B2D5C] text-white rounded-lg font-semibold disabled:opacity-50"
          >
            {validating ? 'Running...' : 'Run Validation'}
          </button>
          {validating && jobStatus && (
            <span className="text-xs text-gray-500">
              {jobStatus.processed}/{jobStatus.total || '?'} — {jobStatus.matched} matched, {jobStatus.noMatch} no match
            </span>
          )}
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[
          { id: 'pending', label: `Pending (${counts.pending})` },
          { id: 'approved', label: `Approved (${counts.approved})` },
          { id: 'skipped', label: `Skipped (${counts.skipped})` },
          { id: 'manual', label: `Manual (${counts.manual})` },
          { id: 'all', label: `All (${items.length})` },
        ].map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1 rounded-full text-sm font-medium transition ${
              filter === f.id ? 'bg-[#1B2D5C] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-gray-500 py-8 text-center">Loading...</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <MapPin className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-lg font-medium">
            {items.length === 0 ? 'No validation data yet' : 'Nothing in this category'}
          </p>
          {items.length === 0 && (
            <p className="text-sm mt-1">Click "Run Validation" to check all customer addresses against the US Census database.</p>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-4 py-3 font-medium text-gray-600">Customer</th>
                <th className="px-4 py-3 font-medium text-gray-600">Current Address</th>
                <th className="px-4 py-3 font-medium text-gray-600">Census Suggestion</th>
                <th className="px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(item => (
                <tr key={item.customerId} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium whitespace-nowrap">{item.customerName}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {item.currentAddress}{item.currentCity ? `, ${item.currentCity}` : ''}{item.currentZip ? ` ${item.currentZip}` : ''}
                  </td>
                  <td className="px-4 py-3">
                    {item.suggestedFull ? (
                      <span className="text-amber-700 font-medium">{item.suggestedFull}</span>
                    ) : (
                      <span className="text-red-400 italic text-xs">No Census match</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      item.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      item.status === 'approved' ? 'bg-green-100 text-green-800' :
                      item.status === 'skipped' ? 'bg-gray-100 text-gray-600' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {item.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {editingId === item.customerId ? (
                      <div className="space-y-1.5 min-w-[220px]">
                        <input
                          className="w-full border rounded px-2 py-1 text-xs"
                          placeholder="Address"
                          value={editValues.address}
                          onChange={e => setEditValues(v => ({ ...v, address: e.target.value }))}
                        />
                        <div className="flex gap-1">
                          <input
                            className="flex-1 border rounded px-2 py-1 text-xs"
                            placeholder="City"
                            value={editValues.city}
                            onChange={e => setEditValues(v => ({ ...v, city: e.target.value }))}
                          />
                          <input
                            className="w-20 border rounded px-2 py-1 text-xs"
                            placeholder="ZIP"
                            value={editValues.zip}
                            onChange={e => setEditValues(v => ({ ...v, zip: e.target.value }))}
                          />
                        </div>
                        <div className="flex gap-1">
                          <button onClick={() => applyManual(item.customerId)} className="px-2 py-1 bg-[#1B2D5C] text-white rounded text-xs">Save</button>
                          <button onClick={() => setEditingId(null)} className="px-2 py-1 border rounded text-xs">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex gap-1 flex-wrap">
                        {item.status === 'pending' && item.suggestedFull && (
                          <button onClick={() => approve(item.customerId)} className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700">Approve</button>
                        )}
                        {item.status === 'pending' && (
                          <button onClick={() => skip(item.customerId)} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs hover:bg-gray-200">Skip</button>
                        )}
                        <button
                          onClick={() => { setEditingId(item.customerId); setEditValues({ address: item.currentAddress || '', city: item.currentCity || '', zip: item.currentZip || '' }); }}
                          className="px-2 py-1 border rounded text-xs hover:bg-gray-50"
                        >Edit</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TrashCollectionApp;
